import { lazy, memo, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  BadgePercent,
  Box,
  Cable,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Clapperboard,
  Clock3,
  Copy,
  Crosshair,
  Download,
  Facebook,
  Film,
  Gamepad2,
  Gem,
  Globe2,
  Headphones,
  ImageUp,
  Instagram,
  Layers3,
  Laptop,
  LockKeyhole,
  LoaderCircle,
  Menu,
  Mail,
  MessageCircle,
  Minus,
  Moon,
  Music2,
  Package,
  Play,
  Plus,
  Radio,
  Search,
  Send,
  Share2,
  Smartphone,
  ShoppingBag,
  ShoppingCart,
  ShieldCheck,
  Speaker,
  Sparkles,
  Store,
  Sun,
  Target,
  Tv,
  X,
  Zap,
} from "lucide-react";
import { DEFAULT_DATA, mergeCatalog, resolveServiceImage } from "./data";
import {
  fetchRemoteCatalog,
  loginWithEmail,
  loginWithGoogle,
  logout,
  publishComment,
  registerWithEmail,
  requestPasswordReset,
  subscribeToComments,
  subscribeToUser,
  trackMetric,
  trackUniqueProductView,
  trackUniqueVisit,
} from "./firebase";
import { applyDocumentTheme, getStoredTheme, THEME_KEY } from "./theme";
import { formatOfferCountdown, getEffectivePrice, getOfferState } from "./offers";
const CACHE_KEY = "divedix-store-data";
const COUNTRY_KEY = "divedix-store-country";
const CART_KEY = "divedix-store-cart";
const SITE_ORIGIN = "https://divedixgroup.com";
const AdminApp = lazy(() => import("./Admin"));

function usePersistentTheme() {
  const [theme, setTheme] = useState(getStoredTheme);

  useLayoutEffect(() => {
    applyDocumentTheme(theme);
  }, [theme]);

  useEffect(() => {
    const syncTheme = (event) => {
      if (event.key === THEME_KEY && event.newValue) setTheme(event.newValue);
    };
    window.addEventListener("storage", syncTheme);
    return () => window.removeEventListener("storage", syncTheme);
  }, []);

  return [theme, setTheme];
}

function ThemeToggle({ theme, setTheme, className = "icon-button" }) {
  return (
    <button
      className={className}
      type="button"
      aria-label={theme === "dark" ? "Activar tema claro" : "Activar tema oscuro"}
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
    >
      {theme === "dark" ? <Sun /> : <Moon />}
    </button>
  );
}

function PerfumeBottleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 2.5h6v4H9z" />
      <path d="M10 6.5v2L7.4 10.7A4 4 0 0 0 6 13.75V20a1.5 1.5 0 0 0 1.5 1.5h9A1.5 1.5 0 0 0 18 20v-6.25a4 4 0 0 0-1.4-3.05L14 8.5v-2" />
      <path d="M8.75 14h6.5M8.75 17.5h6.5" />
    </svg>
  );
}

const waitFor = (duration) => new Promise((resolve) => window.setTimeout(resolve, duration));
const PURCHASE_ANIMATION_DURATION = 1900;

function animateProductToCart(product, source, target) {
  if (!source || !target) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    target.classList.add("is-receiving");
    window.setTimeout(() => target.classList.remove("is-receiving"), 320);
    return;
  }

  const sourceCard = source.closest(".store-product");
  const sourceVisual = sourceCard?.querySelector(".store-product__visual");
  const origin = (sourceVisual || source).getBoundingClientRect();
  const destination = target.getBoundingClientRect();
  const flight = document.createElement("span");
  const visual = sourceVisual?.querySelector("img, svg")?.cloneNode(true);
  const size = Math.min(72, Math.max(48, origin.width * 0.28));
  const startX = origin.left + (origin.width - size) / 2;
  const startY = origin.top + (origin.height - size) / 2;
  const endX = destination.left + destination.width / 2 - size / 2;
  const endY = destination.top + destination.height / 2 - size / 2;

  flight.className = "cart-flight";
  flight.style.setProperty("--flight-size", `${size}px`);
  flight.style.setProperty("--flight-start-x", `${startX}px`);
  flight.style.setProperty("--flight-start-y", `${startY}px`);
  flight.style.setProperty("--flight-x", `${endX - startX}px`);
  flight.style.setProperty("--flight-y", `${endY - startY}px`);
  flight.setAttribute("aria-hidden", "true");
  if (visual) flight.append(visual);
  else flight.textContent = product?.nombre?.trim()?.charAt(0)?.toUpperCase() || "+";
  document.body.append(flight);

  const finish = () => {
    flight.remove();
    target.classList.add("is-receiving");
    window.setTimeout(() => target.classList.remove("is-receiving"), 360);
  };
  flight.addEventListener("animationend", finish, { once: true });
  window.setTimeout(() => flight.isConnected && finish(), 1000);
}

function CheckoutAnimation({ open, itemCount = 1 }) {
  if (!open) return null;
  return createPortal((
    <div className="checkout-animation" role="status" aria-live="polite">
      <div className="checkout-animation__card">
        <div className="checkout-animation__scene" aria-hidden="true">
          <span className="checkout-animation__trail" />
          <span className="checkout-animation__cart"><ShoppingCart /></span>
          <span className="checkout-animation__check"><Check /></span>
        </div>
        <small>PEDIDO PREPARADO</small>
        <strong>Vamos a WhatsApp</strong>
        <p>Un representante continuará contigo.</p>
        <span className="checkout-animation__ready"><Check /> Todo listo</span>
        <em>Abriendo WhatsApp con {itemCount} {itemCount === 1 ? "artículo" : "artículos"}…</em>
      </div>
    </div>
  ), document.body);
}

const scheduleIdleWork = (callback, timeout = 2500) => {
  if ("requestIdleCallback" in window) {
    const id = window.requestIdleCallback(callback, { timeout });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(callback, Math.min(timeout, 1200));
  return () => window.clearTimeout(id);
};

const createOrderNumber = () => {
  const day = new Date().toISOString().slice(2, 10).replaceAll("-", "");
  const random = globalThis.crypto?.randomUUID?.().slice(0, 6).toUpperCase()
    || Math.random().toString(36).slice(2, 8).toUpperCase();
  return `DDX-${day}-${random}`;
};

async function sendOrder({ receiptFile, message, contact, orderNumber }) {
  if (receiptFile && navigator.share && navigator.canShare?.({ files: [receiptFile] })) {
    try {
      await navigator.share({
        title: `Pedido ${orderNumber}`,
        text: `${message}\n\nEnviar a Divedix Store: +1 ${contact.whatsapp}`,
        files: [receiptFile],
      });
      return;
    } catch (caught) {
      if (caught?.name === "AbortError") return;
    }
  }
  await navigator.clipboard?.writeText(message).catch(() => undefined);
  window.open(`https://wa.me/1${contact.whatsapp}?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
}

function whatsappLink(contact, message) {
  const rawNumber = String(contact?.whatsapp || "").replace(/\D/g, "");
  const number = rawNumber.startsWith("1") ? rawNumber : `1${rawNumber}`;
  return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
}

function wholesaleRequestLink(contact, group) {
  const detail = group.items
    .map((item) => `• ${item.name} · ${Number(item.quantity || 1)} ${Number(item.quantity || 1) === 1 ? "unidad" : "unidades"}`)
    .join("\n");
  const message = `*DIVEDIX STORE | COMPRA AL POR MAYOR*\n━━━━━━━━━━━━━━\n▸ *Tipo de producto:* ${group.label}\n▸ *Cantidad total:* ${group.quantity} unidades\n\n*ARTÍCULOS SELECCIONADOS*\n${detail}\n\n*¿CÓMO SE COMPLETA?*\n1. Confirmamos modelos, cantidades e inventario.\n2. Te enviamos la cotización mayorista disponible.\n3. Coordinamos el pago y la entrega contigo.\n━━━━━━━━━━━━━━\n✓ Quiero comprar por mayor y recibir mi cotización.`;
  return whatsappLink(contact, message);
}

function humanizeProductGroup(value) {
  const text = String(value || "productos").replace(/[-_]+/g, " ").trim();
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : "Productos";
}

const categoryMeta = {
  video: { label: "Streaming y video", icon: Clapperboard, eyebrow: "01 / PANTALLA" },
  audio: { label: "Audio premium", icon: Headphones, eyebrow: "02 / SONIDO" },
  iptv: { label: "TV e IPTV", icon: Radio, eyebrow: "03 / SEÑAL" },
};

const storeCategoryMeta = {
  todos: { label: "Todo", icon: Store },
  video: { label: "Video", icon: Tv },
  audio: { label: "Audio", icon: Speaker },
  accesorios: { label: "Accesorios", icon: Cable },
  digitales: { label: "Productos digitales", icon: Laptop },
};

const productCategoryMeta = {
  ...storeCategoryMeta,
  perfumes: { label: "Perfumes", icon: Sparkles },
};

const GAME_ICON_COMPONENTS = {
  crosshair: Crosshair,
  gamepad: Gamepad2,
  gem: Gem,
  shield: ShieldCheck,
  spark: Sparkles,
  target: Target,
};

function useUniqueVisit() {
  useEffect(() => {
    const device = window.matchMedia("(max-width: 760px)").matches ? "movil" : "ordenador";
    return scheduleIdleWork(() => trackUniqueVisit(device), 5000);
  }, []);
}

const SERVICE_SLUGS = {
  netflix: "netflix",
  prime: "amazon-prime-video",
  hbo: "hbo-max",
  disney: "disney-plus",
  ytpremium: "youtube-premium",
  spotify: "spotify",
  ytmusic: "youtube-music",
  iptvsmarters: "iptv-smarters-pro",
  iboplayer: "ibo-player-pro",
};

const SLUG_SERVICES = Object.fromEntries(Object.entries(SERVICE_SLUGS).map(([id, slug]) => [slug, id]));
const DOWNLOAD_PATHS = {
  iptv: "/instalar/iptv-smarters-pro",
  evo: "/instalar/evo-pro-player",
  flujo: "/instalar/flujo-tv",
  telelatino: "/instalar/telelatino",
  "divedix-tv": "/instalar/divedix-tv",
};

const guidePathFor = (id, page) => (
  typeof page?.path === "string" && page.path.startsWith("/instalar/")
    ? page.path
    : DOWNLOAD_PATHS[id] || `/instalar/${id}`
);

const fallbackBrand = {
  netflix: { text: "N", className: "brand-letter brand-netflix" },
  prime: { text: "prime", className: "brand-word brand-prime" },
  hbo: { text: "max", className: "brand-word brand-max" },
  disney: { text: "Disney+", className: "brand-word brand-disney" },
  ytpremium: { text: "▶", className: "brand-letter brand-youtube" },
  spotify: { text: "Spotify", className: "brand-word brand-spotify" },
  ytmusic: { text: "◉", className: "brand-letter brand-youtube" },
  iptvsmarters: { text: "IPTV", className: "brand-word brand-iptv" },
  iboplayer: { text: "IBO", className: "brand-word brand-ibo" },
};

let catalogSnapshot;
let catalogRefreshPromise;

function getInitialCatalog() {
  if (catalogSnapshot) return catalogSnapshot;
  try {
    catalogSnapshot = mergeCatalog(JSON.parse(localStorage.getItem(CACHE_KEY)));
  } catch {
    catalogSnapshot = mergeCatalog(DEFAULT_DATA);
  }
  return catalogSnapshot;
}

function refreshCatalogOnce() {
  if (!catalogRefreshPromise) {
    catalogRefreshPromise = fetchRemoteCatalog()
      .then((remote) => {
        if (!remote) return getInitialCatalog();
        catalogSnapshot = mergeCatalog(remote);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(catalogSnapshot));
        } catch {
          // A full in-memory catalog still works if browser storage is full.
        }
        return catalogSnapshot;
      })
      .catch(() => getInitialCatalog());
  }
  return catalogRefreshPromise;
}

function useCatalog() {
  const [catalog, setCatalog] = useState(getInitialCatalog);

  useEffect(() => {
    let active = true;
    const cancelScheduledWork = scheduleIdleWork(() => {
      refreshCatalogOnce().then((nextCatalog) => active && setCatalog(nextCatalog));
    });
    return () => {
      active = false;
      cancelScheduledWork();
    };
  }, []);

  return catalog;
}

const deferredImageCallbacks = new WeakMap();
let deferredImageObserver;

function observeDeferredImage(node, onVisible) {
  if (!("IntersectionObserver" in window)) {
    onVisible();
    return () => undefined;
  }
  if (!deferredImageObserver) {
    deferredImageObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        deferredImageCallbacks.get(entry.target)?.();
        deferredImageCallbacks.delete(entry.target);
        deferredImageObserver.unobserve(entry.target);
      });
    }, { rootMargin: "700px 0px" });
  }
  deferredImageCallbacks.set(node, onVisible);
  deferredImageObserver.observe(node);
  return () => {
    deferredImageCallbacks.delete(node);
    deferredImageObserver?.unobserve(node);
  };
}

function DeferredImage({ src, eager = false, ...props }) {
  const [ready, setReady] = useState(eager);
  const imageRef = useRef(null);

  useEffect(() => {
    if (eager || !src) return undefined;
    const node = imageRef.current;
    if (!node) return undefined;
    return observeDeferredImage(node, () => setReady(true));
  }, [eager, src]);

  return <img ref={imageRef} src={ready || eager ? src : undefined} loading={eager ? "eager" : "lazy"} decoding="async" {...props} />;
}

function Brand({ compact = false }) {
  return (
    <a className={`site-brand ${compact ? "site-brand--compact" : ""}`} href="/" aria-label="Divedix Store, inicio">
      <span className="brand-glyph" aria-hidden="true">
        <img src="/branding/divedix-mark.png" alt="" />
      </span>
      <span className="brand-name">Divedix <span>Store</span></span>
      <span className="brand-store-dot" aria-hidden="true" />
    </a>
  );
}

function ServiceLogo({ service, size = "large", eager = false }) {
  const [failed, setFailed] = useState(false);
  const image = resolveServiceImage(service);
  const fallback = fallbackBrand[service.id] || {
    text: service.logo || service.nombre.slice(0, 3),
    className: "brand-word",
  };

  return (
    <div className={`service-logo service-logo--${size}`} style={{ "--brand": service.color || "#b8ff65" }}>
      {image && !failed ? (
        <DeferredImage src={image} eager={eager} alt="" onError={() => setFailed(true)} />
      ) : (
        <span className={fallback.className}>{fallback.text}</span>
      )}
    </div>
  );
}

const priceFormatters = new Map();

function Price({ value, country, clean = false }) {
  const amount = Number(value || 0) * Number(country.rate || 1);
  const digits = country.currency === "USD" || ["PEN", "EUR", "GBP", "CAD", "BRL"].includes(country.currency) ? 2 : 0;
  if (!priceFormatters.has(digits)) {
    priceFormatters.set(digits, new Intl.NumberFormat("es-419", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }));
  }
  const formatted = priceFormatters.get(digits).format(amount);

  return (
    <span>
      {country.symbol}
      {formatted}
      {!clean && <small>{country.currency}</small>}
    </span>
  );
}

const valueInUsd = (value, sourceCurrency, countries) => {
  const sourceRate = countries?.find((item) => item.currency === sourceCurrency)?.rate || 1;
  return Number(value || 0) / Number(sourceRate || 1);
};

const effectiveValueInUsd = (item, countries, now = Date.now()) => (
  valueInUsd(getEffectivePrice(item, now), item?.moneda || "USD", countries)
);

function useOfferClock() {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

function OfferPrice({ item, countries, country, now, clean = true }) {
  const state = getOfferState(item, now);
  const basePriceUsd = valueInUsd(state.basePrice, item?.moneda || "USD", countries);
  const activePriceUsd = valueInUsd(state.price, item?.moneda || "USD", countries);
  if (!state.isActive) return <Price value={basePriceUsd} country={country} clean={clean} />;
  return <span className="offer-price"><del><Price value={basePriceUsd} country={country} clean /></del><span><Price value={activePriceUsd} country={country} clean={clean} /></span></span>;
}

function OfferCountdown({ item, now, compact = false }) {
  const state = getOfferState(item, now);
  if (!state.isActive) return null;
  return (
    <span className={compact ? "offer-countdown offer-countdown--compact" : "offer-countdown"}>
      <BadgePercent /> <strong>Oferta −{state.percentage}%</strong>
      <span><Clock3 /> Quedan {formatOfferCountdown(state.remainingMs)}</span>
    </span>
  );
}

const cleanOrderText = (value, limit = 80) => String(value || "")
  .replace(/[\r\n*_~`]+/g, " ")
  .replace(/\s+/g, " ")
  .trim()
  .slice(0, limit);

const referralLine = (hasReferral, referralName) => hasReferral === "yes"
  ? `\n▸ *Referido por:* ${cleanOrderText(referralName)}`
  : "\n▸ *Referido:* No";

function ReferralQuestion({ value, onChange, name, onNameChange }) {
  return (
    <fieldset className="referral-question">
      <legend>¿Vienes de referido?</legend>
      <div className="referral-question__choices">
        <button type="button" className={value === "yes" ? "is-selected" : ""} aria-pressed={value === "yes"} onClick={() => onChange("yes")}>Sí</button>
        <button type="button" className={value === "no" ? "is-selected" : ""} aria-pressed={value === "no"} onClick={() => onChange("no")}>No</button>
      </div>
      {value === "yes" && <label>Nombre de quien te refirió<input value={name} onChange={(event) => onNameChange(event.target.value.slice(0, 80))} placeholder="Escribe su nombre" required autoComplete="off" /></label>}
      <small>{value === "yes" ? "Lo agregaremos a tu pedido." : "Puedes continuar normalmente."}</small>
    </fieldset>
  );
}

function TopBar({ catalog, country, setCountry, theme, setTheme }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navItems = [
    { label: "Streaming", href: "#catalogo" },
    { label: "Store", href: "#store" },
    { label: "Perfumes", href: "/perfumes" },
    { label: "Recargas de juegos", href: "/recargas-juegos", disabled: catalog.gameRechargeConfig?.activo !== true },
    { label: "Sugerir producto", href: "/sugerencias", disabled: catalog.suggestionConfig?.activo === false },
    { label: "Flujo TV", href: "#flujo" },
    { label: "Ayuda", href: "#faq" },
  ];

  return (
    <header className="topbar">
      <Brand />
      <button
        className="icon-button menu-button"
        type="button"
        aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((value) => !value)}
      >
        {menuOpen ? <X /> : <Menu />}
      </button>
      <nav className={menuOpen ? "nav-links nav-links--open" : "nav-links"} aria-label="Navegación principal">
        {navItems.map((item) => item.disabled ? (
          <span className="nav-link-disabled" key={item.href} title="Próximamente" aria-disabled="true">
            {item.label}<small>Pronto</small>
          </span>
        ) : (
          <a key={item.href} href={item.href} onClick={() => setMenuOpen(false)}>
            {item.label}
          </a>
        ))}
      </nav>
      <div className="topbar-actions">
        <label className="country-picker">
          <span className="sr-only">Ver precios para</span>
          <Globe2 className="country-picker__globe" aria-hidden="true" />
          <select value={country.code} onChange={(event) => setCountry(event.target.value)}>
            {catalog.paises.map((item) => (
              <option key={item.code} value={item.code}>
                {item.flag} {item.name}
              </option>
            ))}
          </select>
          <ChevronDown className="country-picker__chevron" aria-hidden="true" />
        </label>
        <ThemeToggle theme={theme} setTheme={setTheme} />
      </div>
    </header>
  );
}

function Hero({ onExplore, onComment }) {
  return (
    <section className="hero" id="inicio">
      <div className="hero-copy">
        <h1 className="hero-title-sales">
          <span>Conecta.</span>
          <em>Disfruta.</em>
          <span>Actualiza tu mundo.</span>
        </h1>
        <p>
          Streaming, audio, video, accesorios y productos digitales elegidos para hacer tu
          vida más simple. Compra o activa todo con atención directa por WhatsApp.
        </p>
        <div className="hero-actions">
          <button className="button button--primary" type="button" onClick={onExplore}>
            Explorar Divedix <ArrowDown />
          </button>
          <a className="text-link" href="#store">
            Ir a la tienda <ArrowUpRight />
          </a>
          <button className="text-link text-link--button" type="button" onClick={onComment}>
            Opinar <MessageCircle />
          </button>
        </div>
        <div className="hero-proof" aria-label="Beneficios">
          <div>
            <strong>4 categorías</strong>
            <span>un solo inventario</span>
          </div>
          <div>
            <strong>Entrega clara</strong>
            <span>sin pasos confusos</span>
          </div>
          <div>
            <strong>Soporte humano</strong>
            <span>antes y después</span>
          </div>
        </div>
      </div>

      <div className="hero-stage" aria-label="Selección de productos de Divedix Store">
        <div className="orbit orbit--one" />
        <div className="orbit orbit--two" />
        <div className="divedix-device">
          <div className="divedix-device__top">
            <span>DIVEDIX / STORE</span>
            <i />
          </div>
          <img src="/branding/divedix-mark.png" alt="" />
          <strong>Todo conectado.</strong>
          <p>Tu centro de tecnología, señal y entretenimiento.</p>
          <div className="divedix-device__grid">
            <span><Tv /> Video</span>
            <span><Speaker /> Audio</span>
            <span><Cable /> Accesorios</span>
            <span><Laptop /> Digital</span>
          </div>
        </div>
        <div className="floating-ticket floating-ticket--top">
          <Zap />
          <span>
            <small>RESPUESTA</small>
            directa
          </span>
        </div>
        <div className="floating-ticket floating-ticket--bottom">
          <Package />
          <span>
            <small>INVENTARIO</small>
            siempre al día
          </span>
        </div>
        <div className="scribble">plug in</div>
      </div>
      <div className="hero-marquee" aria-hidden="true">
        <div>
          VIDEO <Sparkles /> AUDIO <Sparkles /> ACCESORIOS <Sparkles /> PRODUCTOS DIGITALES <Sparkles />
          VIDEO <Sparkles /> AUDIO <Sparkles /> ACCESORIOS <Sparkles /> PRODUCTOS DIGITALES <Sparkles />
        </div>
      </div>
    </section>
  );
}

function SectionIntro({ eyebrow, title, copy, aside }) {
  return (
    <div className="section-intro">
      <div>
        <span className="eyebrow eyebrow--plain">{eyebrow}</span>
        <h2>{title}</h2>
      </div>
      <div className="section-intro__aside">
        {copy && <p>{copy}</p>}
        {aside}
      </div>
    </div>
  );
}

function ServiceCard({ service, country, countries, onChoose, index, now }) {
  const offeredPlans = service.planes.filter((plan) => getOfferState(plan, now).isActive);
  const minPlan = service.planes.reduce((best, plan) => (
    effectiveValueInUsd(plan, countries, now) < effectiveValueInUsd(best, countries, now) ? plan : best
  ), service.planes[0]);
  const minPrice = effectiveValueInUsd(minPlan, countries, now);
  return (
    <article className="service-card" style={{ "--brand": service.color || "#b8ff65", "--delay": `${index * 45}ms` }}>
      <button type="button" onClick={() => onChoose(service)} aria-label={`Ver planes de ${service.nombre}`}>
        <div className="service-card__top">
          <ServiceLogo service={service} />
          <ArrowUpRight />
        </div>
        <div className="service-card__body">
          <span>{categoryMeta[service.categoria]?.label || "Entretenimiento"}</span>
          <h3>{service.nombre}</h3>
          {offeredPlans.length > 0 && <OfferCountdown item={offeredPlans[0]} now={now} compact />}
        </div>
        <div className="service-card__foot">
          <span>Desde</span>
          <strong>
            <Price value={minPrice} country={country} />
          </strong>
          <span>/ mes</span>
        </div>
      </button>
    </article>
  );
}

function Catalog({ catalog, country, onChoose, now }) {
  const [activeCategory, setActiveCategory] = useState("video");
  const services = catalog.services.filter((service) => service.categoria === activeCategory);

  return (
    <section className="page-section catalog-section" id="catalogo">
      <SectionIntro
        eyebrow="SUSCRIPCIONES DIGITALES"
        title="Plataformas listas para conectar."
        copy="Compara cada servicio, revisa sus planes y solicita la activación con los datos claros desde el inicio."
      />
      <div className="category-tabs" role="tablist" aria-label="Categorías de servicios">
        {Object.entries(categoryMeta).map(([id, meta]) => {
          const Icon = meta.icon;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={activeCategory === id}
              className={activeCategory === id ? "category-tab category-tab--active" : "category-tab"}
              onClick={() => setActiveCategory(id)}
            >
              <Icon /> {meta.label}
            </button>
          );
        })}
      </div>
      {activeCategory === "iptv" && (
        <div className="category-extra">
          <div>
            <Download />
            <span>
              <strong>Centro de instalación disponible</strong>
              <small>Encuentra códigos y rutas de instalación para IPTV Smarters Pro, Evo Pro Player y Flujo TV.</small>
            </span>
          </div>
          <a href="#instalacion">Abrir herramientas <ArrowDown /></a>
        </div>
      )}
      <div className="service-grid">
        {services.map((service, index) => (
          <ServiceCard
            key={service.id}
            service={service}
            country={country}
            countries={catalog.paises}
            now={now}
            onChoose={onChoose}
            index={index}
          />
        ))}
      </div>
    </section>
  );
}

function ProductVisual({ product, onOpen, eager = false }) {
  const [failed, setFailed] = useState(false);
  const Icon = productCategoryMeta[product.categoria]?.icon || Box;
  const badge = product.badge || productCategoryMeta[product.categoria]?.label || "Divedix";
  const badgeTone = /disponible|en stock/i.test(badge)
    ? "available"
    : /nuevo|nueva/i.test(badge)
      ? "new"
      : /agotado|sin stock/i.test(badge)
        ? "unavailable"
        : /vendido|popular|trending/i.test(badge)
          ? "popular"
          : "default";
  const content = (
    <>
      {product.imagen && !failed
        ? <DeferredImage src={product.imagen} eager={eager} alt={product.nombre} onError={() => setFailed(true)} />
        : <Icon aria-hidden="true" />}
      <span className={`product-badge product-badge--${badgeTone}`}>{badge}</span>
    </>
  );
  if (onOpen) return <button className="store-product__visual store-product__visual-button" type="button" onClick={onOpen} aria-label={`Ver detalles de ${product.nombre}`}>{content}</button>;
  return <div className="store-product__visual">{content}</div>;
}

function productFeatures(product) {
  if (Array.isArray(product?.caracteristicas)) return product.caracteristicas.map((item) => String(item).trim()).filter(Boolean);
  if (typeof product?.caracteristicas === "string") return product.caracteristicas.split(/\r?\n|•/).map((item) => item.trim()).filter(Boolean);
  return [];
}

function ProductDescription({ children }) {
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const textRef = useRef(null);
  useEffect(() => {
    const node = textRef.current;
    if (!node) return undefined;
    const checkOverflow = () => {
      const lineHeight = Number.parseFloat(window.getComputedStyle(node).lineHeight) || 20;
      setCanExpand(node.scrollHeight > (lineHeight * 5) + 1);
    };
    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(node);
    return () => observer.disconnect();
  }, [children]);
  if (!children) return null;
  return (
    <div className={expanded ? "product-description is-expanded" : "product-description"}>
      <p ref={textRef}>{children}</p>
      {canExpand && <button type="button" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>
        {expanded ? "Ver menos" : "Ver más"} <ChevronDown />
      </button>}
    </div>
  );
}

function StoreSection({ products, folders, countries, country, freeFireActive, onChoose, onOpen, now }) {
  const [activeCategory, setActiveCategory] = useState("todos");
  const [activeFolder, setActiveFolder] = useState("todos");
  const categoryFolders = activeCategory === "todos" ? [] : (folders?.[activeCategory] || []);
  const activeProducts = (products || []).filter((product) => (
    product.activo !== false
    && (activeCategory === "todos" || product.categoria === activeCategory)
    && (activeFolder === "todos" || product.subcategoria === activeFolder)
  ));
  const folderName = (product) => folders?.[product.categoria]?.find((folder) => folder.id === product.subcategoria)?.nombre
    || (product.categoria === "perfumes" ? product.marca || product.familia || "Perfumería" : "");

  return (
    <section className="page-section store-section" id="store">
      <SectionIntro
        eyebrow="DIVEDIX STORE"
        title="Tecnología que sí vas a usar."
        copy="Equipos, accesorios y soluciones digitales con inventario visible y atención directa."
        aside={<span className="store-stock-note"><span /> Inventario actualizado</span>}
      />
      <div className="store-tabs" role="tablist" aria-label="Categorías de la tienda">
        {Object.entries(storeCategoryMeta).map(([id, meta]) => {
          const Icon = meta.icon;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={activeCategory === id}
              className={activeCategory === id ? "is-active" : ""}
              onClick={() => { setActiveCategory(id); setActiveFolder("todos"); }}
            >
              <Icon /> {meta.label}
            </button>
          );
        })}
        <a className="store-tab-link store-tab-link--perfume" href="/perfumes">
          <span className="store-tab-link__icon"><PerfumeBottleIcon /></span>
          <span className="store-tab-link__label">Perfumes</span>
        </a>
        {freeFireActive ? (
          <a className="store-tab-link store-tab-link--freefire" href="/recargas-juegos">
            <span className="store-tab-link__icon"><Gamepad2 /></span>
            <span className="store-tab-link__label">Recargas de juegos</span>
          </a>
        ) : (
          <span className="store-tab-link store-tab-link--freefire is-disabled" aria-disabled="true" title="Sección en desarrollo">
            <span className="store-tab-link__icon"><Gamepad2 /></span>
            <span className="store-tab-link__label">Recargas de juegos</span>
            <small>Pronto</small>
          </span>
        )}
        <a className="store-suggestion-cta" href="/sugerencias">
          <span><Mail /></span>
          <span><small>¿NO ENCUENTRAS LO QUE BUSCAS?</small><strong>Solicítalo aquí</strong></span>
          <ArrowUpRight />
        </a>
      </div>
      {activeCategory !== "todos" && (
        <div className="store-folder-panel">
          <div>
            <small>EXPLORA {productCategoryMeta[activeCategory]?.label?.toUpperCase()}</small>
            <strong>Elige el tipo de producto</strong>
          </div>
          <div className="store-folder-grid" role="tablist" aria-label={`Tipos de ${productCategoryMeta[activeCategory]?.label}`}>
            {[{ id: "todos", nombre: "Ver todo" }, ...categoryFolders].map((folder) => (
              <button
                key={folder.id}
                type="button"
                className={activeFolder === folder.id ? "is-active" : ""}
                onClick={() => setActiveFolder(folder.id)}
              >
                <span>{(products || []).filter((product) => product.activo !== false && product.categoria === activeCategory && (folder.id === "todos" || product.subcategoria === folder.id)).length}</span>
                {folder.nombre}
              </button>
            ))}
          </div>
        </div>
      )}
      {activeProducts.length ? (
        <div className="store-grid" key={`${activeCategory}:${activeFolder}`}>
          {activeProducts.map((product) => (
            <article
              className="store-product"
              key={product.id}
              onClick={(event) => !event.target.closest("button, a") && onOpen(product)}
            >
              <ProductVisual product={product} onOpen={() => onOpen(product)} />
              <OfferCountdown item={product} now={now} compact />
              <div className="store-product__copy">
                <span>{folderName(product) || productCategoryMeta[product.categoria]?.label || product.categoria}</span>
                <button className="store-product__title-button" type="button" onClick={() => onOpen(product)}>
                  <h3>{product.nombre}</h3>
                  <small>Ver detalles completos</small>
                </button>
                <ProductDescription>{product.descripcion}</ProductDescription>
              </div>
              <div className="store-product__footer">
                <div>
                  <small>Precio</small>
                  <strong><OfferPrice item={product} countries={countries} country={country} now={now} /></strong>
                </div>
                <button
                  type="button"
                  disabled={Number(product.stock || 0) <= 0}
                  onClick={(event) => onChoose(product, event.currentTarget)}
                >
                  {Number(product.stock || 0) > 0 ? "Añadir" : "Agotado"} {Number(product.stock || 0) > 0 ? <ShoppingCart /> : null}
                </button>
              </div>
              <small className={Number(product.stock || 0) > 3 ? "store-stock" : "store-stock store-stock--low"}>
                {Number(product.stock || 0) > 0
                  ? `${product.stock} ${product.categoria === "digitales" ? "cupos" : "unidades"} disponibles`
                  : "Temporalmente agotado"}
              </small>
            </article>
          ))}
        </div>
      ) : (
        <div className="store-empty"><Package /><strong>Muy pronto agregaremos productos aquí.</strong></div>
      )}
    </section>
  );
}

function FeaturedMovies({ movies, onSelect }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragX, setDragX] = useState(0);
  const dragState = useRef({ pointerId: null, startX: 0, moved: false });
  const suppressClick = useRef(false);
  if (!movies?.length) return null;

  const move = (direction) => {
    setActiveIndex((current) => (current + direction + movies.length) % movies.length);
  };

  const distanceFromActive = (index) => {
    let distance = index - activeIndex;
    if (distance > movies.length / 2) distance -= movies.length;
    if (distance < -movies.length / 2) distance += movies.length;
    return distance;
  };

  return (
    <section className="page-section movies-section" id="peliculas">
      <SectionIntro
        eyebrow="PANTALLA / RADAR"
        title="Estrenos en el radar."
        copy="Una vista rápida de títulos disponibles dentro de las plataformas que distribuye Divedix Store."
        aside={
          <div className="movie-carousel__controls">
            <button type="button" onClick={() => move(-1)} aria-label="Película anterior"><ChevronLeft /></button>
            <span>{String(activeIndex + 1).padStart(2, "0")} / {String(movies.length).padStart(2, "0")}</span>
            <button type="button" onClick={() => move(1)} aria-label="Película siguiente"><ChevronRight /></button>
          </div>
        }
      />
      <div
        className={dragState.current.pointerId === null ? "movie-carousel" : "movie-carousel is-dragging"}
        role="region"
        aria-label="Carrusel de películas destacadas"
        tabIndex="0"
        style={{ "--drag-offset": `${dragX}px` }}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") move(-1);
          if (event.key === "ArrowRight") move(1);
        }}
        onPointerDown={(event) => {
          if (!event.isPrimary) return;
          dragState.current = { pointerId: event.pointerId, startX: event.clientX, moved: false };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (dragState.current.pointerId !== event.pointerId) return;
          const delta = event.clientX - dragState.current.startX;
          if (Math.abs(delta) > 6) dragState.current.moved = true;
          setDragX(Math.max(-120, Math.min(120, delta)));
        }}
        onPointerUp={(event) => {
          if (dragState.current.pointerId !== event.pointerId) return;
          const delta = event.clientX - dragState.current.startX;
          suppressClick.current = dragState.current.moved;
          if (Math.abs(delta) > 45) move(delta > 0 ? -1 : 1);
          dragState.current = { pointerId: null, startX: 0, moved: false };
          setDragX(0);
          window.setTimeout(() => { suppressClick.current = false; }, 0);
        }}
        onPointerCancel={() => {
          dragState.current = { pointerId: null, startX: 0, moved: false };
          setDragX(0);
        }}
      >
        {movies.map((movie, index) => (
          <button
            type="button"
            className={index === activeIndex ? "movie-card movie-card--active" : "movie-card"}
            key={movie.id || movie.titulo || index}
            style={{
              "--position": Math.max(-2, Math.min(2, distanceFromActive(index))),
              "--movie-index": index,
              zIndex: 5 - Math.abs(distanceFromActive(index)),
              visibility: Math.abs(distanceFromActive(index)) > 2 ? "hidden" : "visible",
            }}
            aria-hidden={Math.abs(distanceFromActive(index)) > 1}
            tabIndex={index === activeIndex ? 0 : -1}
            onClick={() => {
              if (suppressClick.current) return;
              if (index === activeIndex) onSelect(movie);
              else setActiveIndex(index);
            }}
          >
            <div className="movie-poster">
              {movie.imagen ? (
                <DeferredImage src={movie.imagen} alt="" />
              ) : (
                <span><Film /></span>
              )}
              <i><Play fill="currentColor" /></i>
            </div>
            <div className="movie-card__copy">
              <small>EN EL RADAR · 0{index + 1}</small>
              <strong>{movie.titulo}</strong>
              <span>Ver sinopsis <ArrowUpRight /></span>
            </div>
          </button>
        ))}
      </div>
      <div className="movie-carousel__dots" aria-label="Elegir película">
        {movies.map((movie, index) => (
          <button
            key={movie.id || movie.titulo || index}
            type="button"
            className={index === activeIndex ? "is-active" : ""}
            onClick={() => setActiveIndex(index)}
            aria-label={`Ir a ${movie.titulo}`}
          />
        ))}
      </div>
    </section>
  );
}

function ComboBuilder({ catalog, country, onOrder, now }) {
  const [open, setOpen] = useState(true);
  const [selection, setSelection] = useState({});

  const selected = useMemo(
    () =>
      Object.entries(selection)
        .map(([id, planIndex]) => {
          const service = catalog.services.find((item) => item.id === id);
          return service ? { service, planIndex, plan: service.planes[planIndex] } : null;
        })
        .filter(Boolean),
    [selection, catalog.services],
  );

  const subtotal = selected.reduce((sum, item) => sum + effectiveValueInUsd(item.plan, catalog.paises, now), 0);
  const discount =
    catalog.comboDescuentos.find(
      (rule) => selected.length >= rule.minApps && selected.length <= rule.maxApps,
    )?.porcentaje || 0;
  const total = subtotal * (1 - discount / 100);

  const toggle = (service) => {
    setSelection((current) => {
      const next = { ...current };
      if (Object.hasOwn(next, service.id)) {
        delete next[service.id];
      } else {
        const cheapest = service.planes.reduce(
          (best, plan, index, plans) => (effectiveValueInUsd(plan, catalog.paises, now) < effectiveValueInUsd(plans[best], catalog.paises, now) ? index : best),
          0,
        );
        next[service.id] = cheapest;
      }
      return next;
    });
  };

  const setPlan = (id, planIndex) => {
    setSelection((current) => ({ ...current, [id]: Number(planIndex) }));
  };

  return (
    <section className="combo-section" id="combo">
      <div className="combo-shell">
        <button
          className="combo-heading"
          type="button"
          aria-expanded={open}
          aria-controls="combo-content"
          onClick={() => setOpen((value) => !value)}
        >
          <div>
            <span className="eyebrow eyebrow--plain">DIVEDIX LAB / CONFIGURADOR</span>
            <h2>Construye tu paquete digital.</h2>
          </div>
          <div className="combo-heading__deal">
            <strong>−20%</strong>
            <span>desde 5 servicios</span>
            <ChevronDown className={open ? "rotate" : ""} />
          </div>
        </button>

        {open && (
          <div className="combo-content" id="combo-content">
            <div className="combo-choices">
              <p className="step-label">
                <span>01</span> Selecciona tus servicios
              </p>
              <div className="combo-grid">
                {catalog.services.map((service) => {
                  const isSelected = Object.hasOwn(selection, service.id);
                  return (
                    <button
                      key={service.id}
                      type="button"
                      className={isSelected ? "combo-choice combo-choice--selected" : "combo-choice"}
                      onClick={() => toggle(service)}
                      aria-pressed={isSelected}
                    >
                      <ServiceLogo service={service} size="small" />
                      <span>{service.nombre}</span>
                      <i>{isSelected ? <Check /> : <span />}</i>
                    </button>
                  );
                })}
              </div>
            </div>

            <aside className="combo-receipt">
              <div className="receipt-heading">
                <div>
                  <span>PAQUETE DIVEDIX</span>
                  <strong>{selected.length || "—"} servicios</strong>
                </div>
                <ShoppingBag />
              </div>
              {selected.length === 0 ? (
                <div className="empty-combo">
                  <Layers3 />
                  <p>Agrega servicios a este módulo para calcular el precio y el ahorro disponible.</p>
                </div>
              ) : (
                <div className="receipt-items">
                  {selected.map(({ service, planIndex }) => (
                    <div className="receipt-item" key={service.id}>
                      <ServiceLogo service={service} size="tiny" />
                      <div>
                        <strong>{service.nombre}</strong>
                        <OfferCountdown item={service.planes[planIndex]} now={now} compact />
                        <label>
                          <span className="sr-only">Plan para {service.nombre}</span>
                          <select value={planIndex} onChange={(event) => setPlan(service.id, event.target.value)}>
                            {service.planes.map((plan, index) => (
                              <option key={`${service.id}-${plan.nombre}`} value={index}>
                                {plan.nombre}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <button type="button" aria-label={`Quitar ${service.nombre}`} onClick={() => toggle(service)}>
                        <X />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="receipt-total">
                {selected.length > 0 && (
                  <>
                    <div>
                      <span>Subtotal</span>
                      <span>
                        <Price value={subtotal} country={country} clean />
                      </span>
                    </div>
                    <div className={discount ? "discount-row" : "discount-row discount-row--muted"}>
                      <span>Ahorro por paquete</span>
                      <span>{discount ? `−${discount}%` : "Agrega otro servicio"}</span>
                    </div>
                  </>
                )}
                <div className="grand-total">
                  <span>Total estimado</span>
                  <strong>
                    <Price value={total} country={country} clean />
                  </strong>
                </div>
              </div>
              <button
                className="button button--dark"
                type="button"
                disabled={!selected.length}
                onClick={() => onOrder({ type: "combo", selected, subtotal, discount, total })}
              >
                Solicitar este paquete <ArrowUpRight />
              </button>
              <small className="receipt-note">Divedix confirma disponibilidad y pago por WhatsApp.</small>
            </aside>
          </div>
        )}
      </div>
    </section>
  );
}

function FlowPlans({ catalog, country, onChoose, now }) {
  const planFeatures = [
    ["3 dispositivos", "Canales HD y 4K", "Catálogo VOD"],
    ["3 dispositivos", "Soporte prioritario", "Sin cortes comerciales"],
    ["3 dispositivos", "Descuento aplicado", "Renovación simple"],
    ["14 meses de servicio", "3 dispositivos", "Mejor valor del catálogo"],
  ];

  return (
    <section className="page-section flow-section" id="flujo">
      <SectionIntro
        eyebrow="MÓDULO DE SEÑAL"
        title="Televisión en vivo, sin vueltas."
        copy="Canales, deportes y contenido bajo demanda en una sola señal. Compara duración, capacidad y costo."
        aside={
          <a className="text-link" href="#instalacion">
            Configurar señal <ArrowDown />
          </a>
        }
      />
      <div className="flow-grid">
        {catalog.flujoTVPlanes.map((plan, index) => (
            <article
              className={index === catalog.flujoTVPlanes.length - 1 ? "flow-card flow-card--featured" : "flow-card"}
              style={{ "--flow-index": index }}
              key={plan.nombre}
            >
            <div className="flow-card__number">0{index + 1}</div>
            {index === catalog.flujoTVPlanes.length - 1 && <span className="popular-tag">MEJOR ELECCIÓN</span>}
            <span className="flow-card__brand">{plan.servicio || "Flujo TV"}</span>
            <h3>{plan.nombre}</h3>
            <OfferCountdown item={plan} now={now} compact />
            <div className="flow-price">
              <OfferPrice item={plan} countries={catalog.paises} country={country} now={now} />
            </div>
            <ul>
              {(planFeatures[index] || ["3 dispositivos", "Canales HD y 4K", "Soporte incluido"]).map((feature) => (
                <li key={feature}>
                  <Check /> {feature}
                </li>
              ))}
            </ul>
            <button
              className="button flow-card__button"
              type="button"
              onClick={() =>
                onChoose({
                  id: "flujotv",
                  nombre: "Flujo TV",
                  color: "#c5ff5a",
                  logo: "FTV",
                  imagen: "/img/flujo.png",
                  categoria: "iptv",
                  planes: catalog.flujoTVPlanes,
                }, index)
              }
            >
              Elegir plan <ChevronRight />
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

function SetupPlayerIcon({ player }) {
  const [failed, setFailed] = useState(false);
  const Icon = player.icon;

  if (player.logo && !failed) {
    return <DeferredImage src={player.logo} alt="" onError={() => setFailed(true)} />;
  }

  return <Icon />;
}

function PlayerSetup({ catalog }) {
  const [copied, setCopied] = useState("");
  const pageIcons = { iptv: Tv, evo: Play, flujo: Smartphone };
  const pageTags = { iptv: "TV · Android · Móvil", evo: "Smart TV · TV Box", flujo: "TV · Android · PC" };
  const players = Object.entries(catalog.downloadPages || {}).map(([id, page]) => ({
    id,
    ...page,
    icon: pageIcons[id] || Download,
    tag: pageTags[id] || "Instalación guiada",
  }));

  const copyCode = async (player) => {
    try {
      await navigator.clipboard.writeText(player.code);
      trackMetric(`clicks/Descargar_${player.id}_Codigo`);
      setCopied(player.id);
      setTimeout(() => setCopied(""), 1800);
    } catch {
      setCopied("");
    }
  };

  return (
    <section className="page-section setup-section" id="instalacion">
      <SectionIntro
        eyebrow="CENTRO DE HERRAMIENTAS"
        title="Instala, conecta y sigue."
        copy="Cada reproductor tiene su ficha técnica, código Downloader y acceso directo a la guía correspondiente."
      />
      <div className="setup-grid">
        {players.map((player, index) => {
          return (
            <article className="setup-card" key={player.id} style={{ "--setup": player.color, "--setup-index": index }}>
              <div className="setup-card__head">
                <span><SetupPlayerIcon player={player} /></span>
                <small>{player.tag}</small>
              </div>
              <h3>{player.name}</h3>
              <ol>
                {player.steps.map((step) => <li key={step}>{step}</li>)}
              </ol>
              <div className="setup-code">
                <div><small>CÓDIGO DOWNLOADER</small><strong>{player.code}</strong></div>
                <button type="button" onClick={() => copyCode(player)} aria-label={`Copiar código de ${player.name}`}>
                  {copied === player.id ? <Check /> : <Copy />}
                </button>
              </div>
              <a href={guidePathFor(player.id, player)} target="_blank" rel="noreferrer">Ver guía completa <ArrowUpRight /></a>
            </article>
          );
        })}
      </div>
      <div className="setup-help">
        <CircleHelp />
        <p><strong>Soporte de configuración.</strong> Si un dispositivo no conecta, revisamos contigo el punto exacto.</p>
        <a href="#contacto">Abrir soporte <ArrowRight /></a>
      </div>
    </section>
  );
}

function DownloadApp({ pageKey = "", pagePath = "" }) {
  useUniqueVisit();
  const catalog = useCatalog();
  const [theme, setTheme] = usePersistentTheme();
  const pageEntry = Object.entries(catalog.downloadPages || {}).find(([key, item]) => (
    key === pageKey || guidePathFor(key, item) === pagePath
  ));
  const resolvedPageKey = pageEntry?.[0] || pageKey;
  const page = pageEntry?.[1];
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!page) return;
    const canonicalUrl = `${SITE_ORIGIN}${page.path || DOWNLOAD_PATHS[resolvedPageKey] || pagePath}`;
    const canonical = document.querySelector('link[rel="canonical"]');
    const openGraphUrl = document.querySelector('meta[property="og:url"]');
    canonical?.setAttribute("href", canonicalUrl);
    openGraphUrl?.setAttribute("content", canonicalUrl);
    document.title = `${page.name} — Instalación | Divedix Store`;
    window.scrollTo(0, 0);
    return () => {
      document.title = "Divedix Store — Tecnología y entretenimiento";
      canonical?.setAttribute("href", `${SITE_ORIGIN}/`);
      openGraphUrl?.setAttribute("content", `${SITE_ORIGIN}/`);
    };
  }, [page, pagePath, resolvedPageKey]);

  if (!page) return null;
  const whatsapp = `https://wa.me/1${catalog.contacto.whatsapp}?text=${encodeURIComponent(`Hola, necesito ayuda para instalar ${page.name}.`)}`;
  const copyCode = async () => {
    await navigator.clipboard.writeText(page.code);
    trackMetric(`clicks/Descargar_${resolvedPageKey}_Codigo`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="download-page" style={{ "--download-accent": page.color }}>
      <header className="download-topbar">
        <Brand />
        <div className="download-topbar__actions">
          <ThemeToggle theme={theme} setTheme={setTheme} />
          <a href="/#instalacion"><ArrowLeft /> Volver a instalaciones</a>
        </div>
      </header>
      <main>
        <section className="download-hero">
          <div className="download-hero__copy">
            <span className="eyebrow eyebrow--plain">FICHA DE INSTALACIÓN</span>
            <h1>Pon <em>{page.name}</em> en marcha.</h1>
            <p>{page.subtitle}</p>
            <div className="download-hero__actions">
              <button type="button" onClick={copyCode}><Copy /> {copied ? "Código copiado" : "Copiar código"}</button>
              <a href={whatsapp} target="_blank" rel="noreferrer"><MessageCircle /> Pedir ayuda</a>
            </div>
          </div>
          <div className="download-app-card">
            <div className="download-app-logo">
              {page.logo ? <img src={page.logo} alt={`Logo de ${page.name}`} decoding="async" fetchPriority="high" /> : <span>{page.shortName}</span>}
            </div>
            <small>APLICACIÓN</small>
            <h2>{page.name}</h2>
            <div className="download-code-box"><span>CÓDIGO DOWNLOADER</span><strong>{page.code}</strong><button type="button" onClick={copyCode}>{copied ? <Check /> : <Copy />}</button></div>
          </div>
        </section>

        <section className="download-content">
          <div className="download-steps">
            <span className="eyebrow eyebrow--plain">RUTA RÁPIDA</span>
            <h2>Tres movimientos y señal lista.</h2>
            <ol>{page.steps.map((step, index) => <li key={`${step}-${index}`}><span>0{index + 1}</span><p>{step}</p></li>)}</ol>
          </div>
          <aside className="download-devices">
            <h3>Elige tu dispositivo</h3>
            <a href={page.downloadUrl} target="_blank" rel="noreferrer" onClick={() => trackMetric(`clicks/Descargar_${resolvedPageKey}_TV`)}><Tv /><span><strong>Smart TV o TV Box</strong><small>Android TV · Google TV</small></span><ArrowUpRight /></a>
            {page.mobileUrl && <a href={page.mobileUrl} target="_blank" rel="noreferrer" onClick={() => trackMetric(`clicks/Descargar_${resolvedPageKey}_Movil`)}><Smartphone /><span><strong>Celular o tablet</strong><small>Android</small></span><ArrowUpRight /></a>}
            <div><Laptop /><span><strong>¿Usas una computadora?</strong><small>Te ayudamos a elegir la opción correcta.</small></span></div>
          </aside>
        </section>

        <section className="download-support">
          <ShieldCheck />
          <div><span>SOPORTE DE CONFIGURACIÓN</span><h2>¿Algo no conecta? Lo revisamos contigo.</h2><p>Envíanos una captura del punto exacto y continuamos desde ahí.</p></div>
          <a href={whatsapp} target="_blank" rel="noreferrer">Hablar por WhatsApp <ArrowUpRight /></a>
        </section>
      </main>
    </div>
  );
}

function ServiceDetailApp({ serviceId }) {
  useUniqueVisit();
  const catalog = useCatalog();
  const offerNow = useOfferClock();
  const [theme, setTheme] = usePersistentTheme();
  const service = catalog.services.find((item) => item.id === serviceId);
  const [countryCode, setCountryCode] = useState(() => localStorage.getItem(COUNTRY_KEY) || "DO");
  const [order, setOrder] = useState(null);
  const country = catalog.paises.find((item) => item.code === countryCode) || catalog.paises[0];

  useEffect(() => {
    if (!service) return undefined;
    const previousTitle = document.title;
    const description = `${service.nombre}: consulta planes, precios en tu moneda y activación rápida por WhatsApp con Divedix Store.`;
    document.title = `${service.nombre} — Planes y precios | Divedix Store`;
    const meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute("content") || "";
    meta?.setAttribute("content", description);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    const canonicalUrl = `${SITE_ORIGIN}/plataformas/${SERVICE_SLUGS[service.id] || service.id}`;
    canonical.href = canonicalUrl;
    const openGraphUrl = document.querySelector('meta[property="og:url"]');
    const previousOpenGraphUrl = openGraphUrl?.getAttribute("content") || "";
    openGraphUrl?.setAttribute("content", canonicalUrl);
    trackMetric(`clicks/SEO_Plataforma_${service.id}`);
    return () => {
      document.title = previousTitle;
      meta?.setAttribute("content", previousDescription);
      canonical.href = `${SITE_ORIGIN}/`;
      openGraphUrl?.setAttribute("content", previousOpenGraphUrl);
    };
  }, [service]);

  if (!service) return <div className="service-detail-missing"><Brand /><h1>Esta plataforma no está disponible.</h1><a href="/">Volver al catálogo</a></div>;

  return (
    <div className="service-detail" style={{ "--service-accent": service.color || "#1261d5" }}>
      <header className="download-topbar">
        <Brand />
        <div className="service-detail__top-actions">
          <label><span className="sr-only">País y moneda</span><select value={country.code} onChange={(event) => { setCountryCode(event.target.value); localStorage.setItem(COUNTRY_KEY, event.target.value); }}>{catalog.paises.map((item) => <option key={item.code} value={item.code}>{item.flag} {item.currency}</option>)}</select></label>
          <ThemeToggle theme={theme} setTheme={setTheme} />
          <a href="/#catalogo"><ArrowLeft /> Catálogo</a>
        </div>
      </header>
      <main>
        <section className="service-detail__hero">
          <div className="service-detail__brand"><ServiceLogo service={service} eager /><span>PLATAFORMA DISPONIBLE</span></div>
          <h1>{service.nombre}, <em>listo para activar.</em></h1>
          <p>Elige el plan que mejor te convenga. Confirmamos disponibilidad, pago y activación directamente contigo por WhatsApp.</p>
        </section>
        <section className="service-detail__plans">
          <div><span className="eyebrow eyebrow--plain">PLANES DISPONIBLES</span><h2>Elige cómo quieres verlo.</h2></div>
          <div className="service-detail__plan-grid">{service.planes.map((plan, index) => <article key={plan.nombre}><span>0{index + 1}</span><h3>{plan.nombre}</h3><OfferCountdown item={plan} now={offerNow} compact /><strong><OfferPrice item={plan} countries={catalog.paises} country={country} now={offerNow} /></strong><ul><li><Check /> Activación rápida</li><li><Check /> Soporte por WhatsApp</li><li><Check /> Precio en {country.currency}</li></ul><button type="button" onClick={() => setOrder({ type: "service", service, planIndex: index })}>Elegir este plan <ArrowUpRight /></button></article>)}</div>
        </section>
        <section className="service-detail__assurance"><ShieldCheck /><div><h2>Compra acompañada, no a ciegas.</h2><p>Antes de pagar te confirmamos exactamente qué incluye el plan y el tiempo de activación.</p></div><a href="/#faq">Ver preguntas frecuentes</a></section>
      </main>
      <OrderModal order={order} country={country} countries={catalog.paises} contact={catalog.contacto} paymentMethods={catalog.metodosPago} now={offerNow} onClose={() => setOrder(null)} />
    </div>
  );
}

function Testimonials({ onOpenComment }) {
  const [comments, setComments] = useState([]);
  const trackRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    let unsubscribe = () => undefined;
    const subscribe = () => {
      unsubscribe = subscribeToComments(setComments, () => setComments([]));
    };
    if (!("IntersectionObserver" in window) || !sectionRef.current) {
      subscribe();
      return () => unsubscribe();
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      subscribe();
    }, { rootMargin: "600px 0px" });
    observer.observe(sectionRef.current);
    return () => {
      observer.disconnect();
      unsubscribe();
    };
  }, []);

  const scroll = (direction) =>
    trackRef.current?.scrollBy({ left: direction * 360, behavior: "smooth" });

  return (
    <section className="testimonials-section" id="comentarios" ref={sectionRef}>
      <div className="page-section testimonials-inner">
        <SectionIntro
          eyebrow="VOCES DE LA COMUNIDAD"
          title="Así se vive Divedix."
          aside={
            <button className="text-link text-link--button" type="button" onClick={onOpenComment}>
              Dejar mi opinión <MessageCircle />
            </button>
          }
        />
        {comments.length > 0 && <div className="testimonial-controls">
          <button type="button" aria-label="Comentarios anteriores" onClick={() => scroll(-1)}>
            <ArrowLeft />
          </button>
          <button type="button" aria-label="Comentarios siguientes" onClick={() => scroll(1)}>
            <ArrowRight />
          </button>
        </div>}
        {comments.length ? <div className="testimonial-track" ref={trackRef}>
          {comments.map((comment, index) => (
            <article className="testimonial-card" key={comment.key || comment.id || index}>
              <div className="quote-mark">“</div>
              <p>{comment.texto}</p>
              <div className="testimonial-author">
                {comment.foto ? (
                  <img src={comment.foto} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" />
                ) : (
                  <span>{comment.nombre?.charAt(0).toUpperCase()}</span>
                )}
                <div>
                  <strong>{comment.nombre}</strong>
                  <small>Cliente de Divedix Store</small>
                </div>
              </div>
            </article>
          ))}
        </div> : (
          <div className="comments-empty">
            <MessageCircle />
            <div><strong>Todavía no hay opiniones publicadas.</strong><span>La primera puede ser la tuya.</span></div>
            <button className="button button--outline" type="button" onClick={onOpenComment}>Escribir opinión</button>
          </div>
        )}
      </div>
    </section>
  );
}

function Faq({ items }) {
  const [openIndex, setOpenIndex] = useState(0);
  return (
    <section className="page-section faq-section" id="faq">
      <div className="faq-aside">
        <CircleHelp />
        <span className="eyebrow eyebrow--plain">MESA DE AYUDA</span>
        <h2>Respuestas antes de comprar.</h2>
        <p>Disponibilidad, entrega, productos digitales y métodos de pago explicados sin letra pequeña.</p>
      </div>
      <div className="faq-list">
        {items.map((item, index) => {
          const isOpen = index === openIndex;
          return (
            <article className={isOpen ? "faq-item faq-item--open" : "faq-item"} key={item.pregunta}>
              <button type="button" onClick={() => setOpenIndex(isOpen ? -1 : index)} aria-expanded={isOpen}>
                <span>0{index + 1}</span>
                <strong>{item.pregunta}</strong>
                <i>{isOpen ? <X /> : <ChevronDown />}</i>
              </button>
              {isOpen && <p>{item.respuesta}</p>}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function Footer({ contact, socialLinks = {} }) {
  const whatsapp = `https://wa.me/1${contact.whatsapp}?text=${encodeURIComponent("Hola, quiero saber más sobre Divedix Store")}`;
  const networks = [
    { id: "tiktok", label: "TikTok", icon: Music2 },
    { id: "facebook", label: "Facebook", icon: Facebook },
    { id: "instagram", label: "Instagram", icon: Instagram },
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, fallback: whatsapp },
  ].filter((network) => socialLinks[network.id]?.activo !== false);
  return (
    <footer className="footer" id="contacto">
      <div className="footer-cta">
        <span className="eyebrow eyebrow--plain">CANAL DIRECTO</span>
        <h2>Equipo nuevo, señal lista o soporte: escríbenos.</h2>
        <a className="button button--lime" href={whatsapp} target="_blank" rel="noreferrer">
          Hablar por WhatsApp <ArrowUpRight />
        </a>
      </div>
      <div className="footer-bottom">
        <Brand compact />
        <p>Tecnología, señal y soporte en un mismo lugar.</p>
        {networks.length > 0 && <div className="footer-socials" aria-label="Redes sociales">
          {networks.map((network) => {
            const Icon = network.icon;
            const url = socialLinks[network.id]?.url || network.fallback;
            return url
              ? <a key={network.id} href={url} target="_blank" rel="noreferrer" aria-label={network.label} title={network.label}><Icon /><span>{network.label}</span></a>
              : <span className="is-unconfigured" key={network.id} aria-label={`${network.label} pendiente de configurar`} title="Enlace pendiente de configurar"><Icon /><span>{network.label}</span></span>;
          })}
        </div>}
        <div className="footer-nav">
          <a href="/#store">Store</a>
          <a href="/#catalogo">Plataformas</a>
          <a href="/#faq">Ayuda</a>
          <a href="/sugerencias">Sugerir producto</a>
        </div>
        <small>© {new Date().getFullYear()} Divedix Store</small>
      </div>
    </footer>
  );
}

function FloatingWhatsApp({ contact }) {
  const rawNumber = String(contact?.whatsapp || "").replace(/\D/g, "");
  if (!rawNumber) return null;
  const number = rawNumber.startsWith("1") ? rawNumber : `1${rawNumber}`;
  const href = `https://wa.me/${number}?text=${encodeURIComponent("Hola, necesito ayuda con Divedix Store.")}`;
  return <aside className="floating-whatsapp"><span>Buenos días, ¿necesitas ayuda?</span><a href={href} target="_blank" rel="noreferrer" aria-label="Solicitar ayuda por WhatsApp" onClick={() => trackMetric("clicks/WhatsApp_Ayuda_Flotante")}><MessageCircle /></a></aside>;
}

function PublicExtras({ children }) {
  const catalog = useCatalog();
  return <>{children}<FloatingWhatsApp contact={catalog.contacto} /></>;
}

function SuggestionApp() {
  useUniqueVisit();
  const catalog = useCatalog();
  const config = catalog.suggestionConfig || {};
  const [theme, setTheme] = usePersistentTheme();
  const [countryCode, setCountryCode] = useState(() => localStorage.getItem(COUNTRY_KEY) || "DO");
  const [form, setForm] = useState({ nombre: "", telefono: "", correo: "", producto: "", tipo: "Producto físico", fecha: "", presupuesto: "", detalles: "" });
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const country = catalog.paises.find((item) => item.code === countryCode) || catalog.paises[0];
  const recipient = String(config.email || "").trim();

  useEffect(() => {
    document.title = "Sugerir un producto | Divedix Store";
    const canonical = document.querySelector('link[rel="canonical"]');
    const openGraphUrl = document.querySelector('meta[property="og:url"]');
    canonical?.setAttribute("href", `${SITE_ORIGIN}/sugerencias`);
    openGraphUrl?.setAttribute("content", `${SITE_ORIGIN}/sugerencias`);
    window.scrollTo(0, 0);
    return () => {
      document.title = "Divedix Store — Tecnología y entretenimiento";
      canonical?.setAttribute("href", `${SITE_ORIGIN}/`);
      openGraphUrl?.setAttribute("content", `${SITE_ORIGIN}/`);
    };
  }, []);

  const changeCountry = (code) => {
    setCountryCode(code);
    localStorage.setItem(COUNTRY_KEY, code);
  };
  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const submitSuggestion = async (event) => {
    event.preventDefault();
    if (!recipient) {
      setStatus("error");
      setMessage("El correo receptor todavía debe configurarse desde el panel de administración.");
      return;
    }
    setStatus("sending");
    setMessage("");
    try {
      const response = await fetch(`https://formsubmit.co/ajax/${encodeURIComponent(recipient)}`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          _subject: `Nueva solicitud de producto: ${form.producto}`,
          _template: "table",
          Nombre: form.nombre,
          Telefono: form.telefono,
          Correo: form.correo || "No indicado",
          Producto: form.producto,
          Tipo: form.tipo,
          "Fecha requerida": form.fecha || "Sin fecha específica",
          Presupuesto: form.presupuesto || "No indicado",
          Detalles: form.detalles,
          Origen: "Divedix Store",
        }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || result?.success === false) throw new Error("send_failed");
      trackMetric("clicks/Enviar_Sugerencia_Producto");
      setStatus("success");
      setMessage("Solicitud enviada. Te contactaremos cuando revisemos el producto.");
      setForm({ nombre: "", telefono: "", correo: "", producto: "", tipo: "Producto físico", fecha: "", presupuesto: "", detalles: "" });
    } catch {
      setStatus("error");
      setMessage("No pudimos enviar la solicitud ahora. Inténtalo nuevamente o usa el botón de WhatsApp.");
    }
  };

  return <div className="special-page suggestion-page">
    <StandaloneTopbar catalog={catalog} country={country} setCountry={changeCountry} theme={theme} setTheme={setTheme} />
    {!config.activo ? <main className="feature-locked"><div><span><Mail /></span><small>FORMULARIO NO DISPONIBLE</small><h1>Las sugerencias están pausadas.</h1><a className="button button--primary" href="/"><ArrowLeft /> Volver a la tienda</a></div></main> : <main className="suggestion-main">
      <section className="suggestion-intro"><span className="special-kicker"><Mail /> SOLICITUD PERSONALIZADA</span><h1>{config.titulo}</h1><p>{config.descripcion}</p><div><span><strong>01</strong> Cuéntanos qué buscas</span><span><strong>02</strong> Revisamos disponibilidad</span><span><strong>03</strong> Te contactamos</span></div></section>
      <form className="suggestion-form" onSubmit={submitSuggestion}><div className="suggestion-form__head"><span><Send /></span><div><small>NUEVA SOLICITUD</small><h2>Describe lo que necesitas.</h2></div></div><div className="suggestion-form__grid"><label>Nombre completo<input value={form.nombre} onChange={(event) => updateField("nombre", event.target.value)} required /></label><label>Teléfono / WhatsApp<input inputMode="tel" value={form.telefono} onChange={(event) => updateField("telefono", event.target.value)} required /></label><label>Correo electrónico<input type="email" value={form.correo} onChange={(event) => updateField("correo", event.target.value)} /></label><label>Producto que necesitas<input value={form.producto} onChange={(event) => updateField("producto", event.target.value)} placeholder="Nombre, marca o referencia" required /></label><label>Tipo de solicitud<select value={form.tipo} onChange={(event) => updateField("tipo", event.target.value)}><option>Producto físico</option><option>Producto digital</option><option>Accesorio</option><option>Servicio o instalación</option><option>Otro</option></select></label><label>¿Para cuándo lo necesitas?<input type="date" value={form.fecha} onChange={(event) => updateField("fecha", event.target.value)} /></label><label className="suggestion-form__wide">Presupuesto aproximado<input value={form.presupuesto} onChange={(event) => updateField("presupuesto", event.target.value)} placeholder="Opcional" /></label><label className="suggestion-form__wide">Detalles<textarea rows="5" value={form.detalles} onChange={(event) => updateField("detalles", event.target.value)} placeholder="Cantidad, tamaño, color, modelo, región o cualquier detalle importante" required /></label></div>{message && <p className={`suggestion-status is-${status}`} role="status">{message}</p>}<button className="button button--primary" type="submit" disabled={status === "sending"}>{status === "sending" ? <LoaderCircle /> : <Send />} {status === "sending" ? "Enviando…" : "Enviar solicitud"}</button><small className="suggestion-privacy">Usaremos estos datos únicamente para responder a esta solicitud.</small></form>
    </main>}
    <Footer contact={catalog.contacto} socialLinks={catalog.socialLinks} />
  </div>;
}

function ModalShell({ open, onClose, label, className = "", children }) {
  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", closeOnEscape);
    document.body.classList.add("modal-open");
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.body.classList.remove("modal-open");
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className={`modal-card ${className}`.trim()} role="dialog" aria-modal="true" aria-label={label}>
        <button className="modal-close" type="button" onClick={onClose} aria-label="Cerrar">
          <X />
        </button>
        {children}
      </section>
    </div>
  );
}

function ReceiptUpload({ file, onChange, orderNumber, step = 3 }) {
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!file) {
      setPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const selectFile = (event) => {
    const next = event.target.files?.[0];
    if (!next) return;
    if (!next.type.startsWith("image/")) {
      setError("Selecciona una foto o captura válida.");
      return;
    }
    if (next.size > 10 * 1024 * 1024) {
      setError("La imagen no puede pesar más de 10 MB.");
      return;
    }
    setError("");
    onChange(next);
  };

  return (
    <fieldset className="checkout-options receipt-upload">
      <legend><span>{step}</span> Adjunta el comprobante</legend>
      <div className={file ? "receipt-upload__box has-file" : "receipt-upload__box"}>
        {preview ? <img src={preview} alt="Vista previa del comprobante" decoding="async" /> : <ImageUp />}
        <div><strong>{file ? file.name : "Enviar foto del pago"}</strong><small>Pedido {orderNumber} · JPG, PNG o WEBP · máximo 10 MB</small></div>
        <label><input type="file" accept="image/*" onChange={selectFile} /><span>{file ? "Cambiar" : "Elegir foto"}</span></label>
        {file && <button type="button" onClick={() => onChange(null)} aria-label="Quitar comprobante"><X /></button>}
      </div>
      {error && <p className="form-error">{error}</p>}
      <small className="receipt-upload__help">En teléfonos compatibles, la foto y el número de pedido se comparten juntos. En computadora, WhatsApp abrirá el pedido y deberás adjuntar la imagen seleccionada.</small>
    </fieldset>
  );
}

function PaymentSelector({ methods, paymentId, onSelect, step = 2 }) {
  const [copied, setCopied] = useState(false);
  const available = (methods || []).filter((method) => method.activo !== false);
  const selected = available.find((method) => method.id === paymentId) || available[0];
  if (!selected) return null;

  const copyAccount = async () => {
    if (!selected.cuenta) return;
    await navigator.clipboard.writeText(selected.cuenta);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <fieldset className="checkout-options checkout-payment">
      <legend><span>{step}</span> Selecciona el método de pago</legend>
      <div className="payment-method-grid">
        {available.map((method) => (
          <button type="button" key={method.id} className={selected.id === method.id ? "is-selected" : ""} onClick={() => onSelect(method.id)}>
            {selected.id === method.id && <Check />} {method.nombre}
          </button>
        ))}
      </div>
      <div className="payment-detail" aria-live="polite">
        <div className="payment-detail__head"><span>DATOS PARA {selected.nombre.toUpperCase()}</span><strong>{selected.tipo}</strong></div>
        {selected.cuenta ? <div className="payment-detail__account"><span><small>CUENTA / USUARIO</small><strong>{selected.cuenta}</strong></span><button type="button" onClick={copyAccount}>{copied ? <Check /> : <Copy />} {copied ? "Copiado" : "Copiar"}</button></div> : <p>Los datos de esta opción todavía no están publicados. Recibirás la información vigente al confirmar por WhatsApp.</p>}
        {selected.titular && <div className="payment-detail__holder"><small>TITULAR</small><strong>{selected.titular}</strong></div>}
        {selected.instruccion && <small className="payment-detail__note">{selected.instruccion}</small>}
      </div>
    </fieldset>
  );
}

function OrderModal({ order, country, countries, contact, paymentMethods, onAddToCart, onClose, now = Date.now() }) {
  const [planIndex, setPlanIndex] = useState(order?.planIndex || 0);
  const [name, setName] = useState("");
  const [hasReferral, setHasReferral] = useState("no");
  const [referralName, setReferralName] = useState("");
  const [paymentId, setPaymentId] = useState(() => paymentMethods?.find((method) => method.activo !== false)?.id || "");
  const [orderCountryCode, setOrderCountryCode] = useState(country.code);
  const [error, setError] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [orderNumber, setOrderNumber] = useState(createOrderNumber);
  const [isPreparingOrder, setIsPreparingOrder] = useState(false);

  useEffect(() => {
    setPlanIndex(order?.planIndex || 0);
    setOrderCountryCode(country.code);
    setPaymentId((current) => paymentMethods?.some((method) => method.id === current && method.activo !== false)
      ? current
      : paymentMethods?.find((method) => method.activo !== false)?.id || "");
    setReceiptFile(null);
    setOrderNumber(createOrderNumber());
    setError("");
    setHasReferral("no");
    setReferralName("");
  }, [order, country.code, paymentMethods]);

  useEffect(() => {
    setIsPreparingOrder(false);
  }, [order]);

  if (!order) return null;
  const isCombo = order.type === "combo";
  const plan = !isCombo ? order.service.planes[planIndex] : null;
  const comboSubtotal = isCombo ? order.selected.reduce((sum, item) => sum + effectiveValueInUsd(item.plan, countries, now), 0) : 0;
  const total = isCombo ? comboSubtotal * (1 - Number(order.discount || 0) / 100) : effectiveValueInUsd(plan, countries, now);
  const orderCountry = countries.find((item) => item.code === orderCountryCode) || country;
  const selectedPayment = paymentMethods?.find((method) => method.id === paymentId);

  const submit = async (event) => {
    event.preventDefault();
    if (isPreparingOrder) return;
    let details = "";
    if (isCombo) {
      const lines = order.selected
        .map(({ service, plan: selectedPlan }) => `• ${service.nombre} — ${selectedPlan.nombre}`)
        .join("\n");
      details = `▸ *Servicios incluidos:*\n${lines}\n▸ *Ahorro aplicado:* ${order.discount}%`;
    }
    const totalText = `${orderCountry.symbol}${new Intl.NumberFormat("es-419", { maximumFractionDigits: 2 }).format(
      total * orderCountry.rate,
    )} ${orderCountry.currency}`;
    const customer = name.trim() ? `\n▸ *Cliente:* ${cleanOrderText(name)}` : "";
    const referredBy = referralLine(hasReferral, referralName);
    const serviceLine = isCombo ? "Paquete digital Divedix" : order.service.nombre;
    const planLine = isCombo ? `${order.selected.length} servicios` : plan.nombre;
    const receiptLine = receiptFile ? "\n▸ *Comprobante:* Seleccionado para adjuntar" : "\n▸ *Comprobante:* Pendiente";
    const message = `*DIVEDIX STORE | SOLICITUD DIGITAL*\n━━━━━━━━━━━━━━\n▸ *Pedido:* ${orderNumber}\n▸ *Servicio:* ${serviceLine}\n▸ *Plan:* ${planLine}\n▸ *Precio:* ${totalText}\n▸ *Pago elegido:* ${selectedPayment?.nombre || "Por confirmar"}${receiptLine}${customer}${referredBy}\n━━━━━━━━━━━━━━\n${isCombo ? `${details}\n\n` : ""}✓ Solicito verificar el pago, la oferta vigente y validar mi activación.`;
    trackMetric(`clicks/WhatsApp_Compra_${isCombo ? "Combo" : order.service.id}`);
    setError("");
    setIsPreparingOrder(true);
    await waitFor(PURCHASE_ANIMATION_DURATION);
    try {
      await sendOrder({ receiptFile, message, contact, orderNumber });
    } catch {
      setError("No pudimos abrir el envío. Inténtalo nuevamente o usa el botón de contacto.");
    } finally {
      setIsPreparingOrder(false);
    }
  };

  return (
    <ModalShell open={Boolean(order)} onClose={onClose} label="Completar activación">
      <div className="modal-kicker">ÚLTIMO PASO</div>
      <span className="order-number">PEDIDO {orderNumber}</span>
      <div className="modal-title-row">
        {!isCombo && <ServiceLogo service={order.service} size="small" />}
        <div>
          <h2>{isCombo ? "Tu paquete digital" : order.service.nombre}</h2>
          <p>Elige tus opciones. Te llevamos a WhatsApp con el pedido listo.</p>
        </div>
      </div>
      <form className="order-form" onSubmit={submit}>
        {!isCombo && (
          <fieldset className="checkout-options">
            <legend><span>1</span> Elige el plan</legend>
            <div className="checkout-plan-grid">
              {order.service.planes.map((item, index) => (
                <button
                  key={item.nombre}
                  type="button"
                  className={planIndex === index ? "checkout-plan is-selected" : "checkout-plan"}
                  onClick={() => setPlanIndex(index)}
                >
                  <span>{item.nombre}</span>
                  <strong><OfferPrice item={item} countries={countries} country={orderCountry} now={now} /></strong>
                  <OfferCountdown item={item} now={now} compact />
                  {planIndex === index && <Check />}
                </button>
              ))}
            </div>
          </fieldset>
        )}
        {isCombo && (
          <div className="modal-combo-list">
            {order.selected.map(({ service, plan: selectedPlan }) => (
              <div key={service.id}>
                <span>{service.nombre}</span>
                <small>{selectedPlan.nombre}</small>
              </div>
            ))}
          </div>
        )}
        <div className="checkout-details">
          <label>
            País y moneda
            <select value={orderCountryCode} onChange={(event) => setOrderCountryCode(event.target.value)}>
              {countries.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.flag} {item.name} · {item.currency}
                </option>
              ))}
            </select>
          </label>
          <label>
            Tu nombre <small>(opcional)</small>
            <input value={name} onChange={(event) => setName(event.target.value)} placeholder="¿Cómo te llamas?" autoComplete="name" />
          </label>
        </div>
        <ReferralQuestion value={hasReferral} onChange={setHasReferral} name={referralName} onNameChange={setReferralName} />
        <PaymentSelector methods={paymentMethods} paymentId={paymentId} onSelect={setPaymentId} />
        <ReceiptUpload file={receiptFile} onChange={setReceiptFile} orderNumber={orderNumber} />
        {error && <p className="form-error">{error}</p>}
        <div className="modal-total checkout-total">
          <span>Total de este pedido</span>
          <strong>
            <Price value={total} country={orderCountry} clean />
          </strong>
        </div>
        {!isCombo && onAddToCart && (
          <button
            className="button button--cart"
            type="button"
            onClick={(event) => {
              onAddToCart(order.service, planIndex, event.currentTarget);
              onClose();
            }}
          >
            <ShoppingCart /> Añadir este plan al carrito
          </button>
        )}
        <button className="button button--whatsapp" type="submit" disabled={isPreparingOrder}>
          {isPreparingOrder ? <LoaderCircle /> : <MessageCircle />} {isPreparingOrder ? "Preparando pedido…" : receiptFile ? "Enviar comprobante y pedido" : "Enviar pedido por WhatsApp"}
        </button>
        <small className="checkout-note">Después de transferir, adjunta el comprobante. Divedix verificará el pago usando el número de pedido.</small>
      </form>
      <CheckoutAnimation open={isPreparingOrder} itemCount={isCombo ? order.selected.length : 1} />
    </ModalShell>
  );
}

function ProductOrderModal({ product, country, countries, contact, paymentMethods, onClose, now = Date.now() }) {
  const [name, setName] = useState("");
  const [hasReferral, setHasReferral] = useState("no");
  const [referralName, setReferralName] = useState("");
  const [orderCountryCode, setOrderCountryCode] = useState(country.code);
  const [paymentId, setPaymentId] = useState(() => paymentMethods?.find((method) => method.activo !== false)?.id || "");
  const [receiptFile, setReceiptFile] = useState(null);
  const [orderNumber, setOrderNumber] = useState(createOrderNumber);
  const [sendError, setSendError] = useState("");
  const [isPreparingOrder, setIsPreparingOrder] = useState(false);

  useEffect(() => {
    setOrderCountryCode(country.code);
    setPaymentId((current) => paymentMethods?.some((method) => method.id === current && method.activo !== false)
      ? current
      : paymentMethods?.find((method) => method.activo !== false)?.id || "");
    setReceiptFile(null);
    setOrderNumber(createOrderNumber());
    setSendError("");
    setHasReferral("no");
    setReferralName("");
  }, [product, country.code, paymentMethods]);

  useEffect(() => {
    setIsPreparingOrder(false);
  }, [product]);

  if (!product) return null;
  const orderCountry = countries.find((item) => item.code === orderCountryCode) || country;
  const productPriceUsd = effectiveValueInUsd(product, countries, now);
  const selectedPayment = paymentMethods?.find((method) => method.id === paymentId);
  const askForProduct = async (event) => {
    event.preventDefault();
    if (isPreparingOrder) return;
    const converted = productPriceUsd * Number(orderCountry.rate || 1);
    const price = `${orderCountry.symbol}${new Intl.NumberFormat("es-419", { maximumFractionDigits: 2 }).format(converted)} ${orderCountry.currency}`;
    const customer = name.trim() ? `\n▸ *Cliente:* ${cleanOrderText(name)}` : "";
    const referredBy = referralLine(hasReferral, referralName);
    const receiptLine = receiptFile ? "\n▸ *Comprobante:* Seleccionado para adjuntar" : "\n▸ *Comprobante:* Pendiente";
    const message = `*DIVEDIX STORE | SOLICITUD DE PRODUCTO*\n━━━━━━━━━━━━━━\n▸ *Pedido:* ${orderNumber}\n▸ *Producto:* ${product.nombre}\n▸ *Categoría:* ${productCategoryMeta[product.categoria]?.label || product.categoria}\n▸ *Precio vigente:* ${price}\n▸ *Pago elegido:* ${selectedPayment?.nombre || "Por confirmar"}${receiptLine}${customer}${referredBy}\n━━━━━━━━━━━━━━\n✓ Solicito verificar el pago, la oferta vigente, confirmar existencia y validar mi pedido.`;
    trackMetric(`clicks/WhatsApp_Store_${product.id}`);
    setSendError("");
    setIsPreparingOrder(true);
    await waitFor(PURCHASE_ANIMATION_DURATION);
    try {
      await sendOrder({ receiptFile, message, contact, orderNumber });
    } catch {
      setSendError("No pudimos abrir el envío. Inténtalo nuevamente.");
    } finally {
      setIsPreparingOrder(false);
    }
  };

  return (
    <ModalShell open={Boolean(product)} onClose={onClose} label={`Comprar ${product.nombre}`} className="modal-card--product">
      <div className="modal-kicker">DIVEDIX STORE</div>
      <span className="order-number">PEDIDO {orderNumber}</span>
      <div className="product-modal-head">
        <ProductVisual key={product.id} product={product} eager />
        <div>
          <span>{productCategoryMeta[product.categoria]?.label || product.categoria}</span>
          <h2>{product.nombre}</h2>
          <p>{product.descripcion}</p>
        </div>
      </div>
      <div className="product-modal-summary">
        <div><span>Precio</span><strong><OfferPrice item={product} countries={countries} country={orderCountry} now={now} /></strong><OfferCountdown item={product} now={now} compact /></div>
        <div><span>Disponibilidad</span><strong>{product.stock} {product.categoria === "digitales" ? "cupos" : "unidades"}</strong></div>
      </div>
      <form className="order-form product-order-form" onSubmit={askForProduct}>
        <div className="checkout-details">
          <label>
            País y moneda
            <select value={orderCountryCode} onChange={(event) => setOrderCountryCode(event.target.value)}>
              {countries.map((item) => <option key={item.code} value={item.code}>{item.flag} {item.name} · {item.currency}</option>)}
            </select>
          </label>
          <label>
            Tu nombre <small>(opcional)</small>
            <input value={name} onChange={(event) => setName(event.target.value)} placeholder="¿Cómo te llamas?" autoComplete="name" />
          </label>
        </div>
        <ReferralQuestion value={hasReferral} onChange={setHasReferral} name={referralName} onNameChange={setReferralName} />
        <PaymentSelector methods={paymentMethods} paymentId={paymentId} onSelect={setPaymentId} step={1} />
        <ReceiptUpload file={receiptFile} onChange={setReceiptFile} orderNumber={orderNumber} step={2} />
        {sendError && <p className="form-error">{sendError}</p>}
        <div className="modal-total checkout-total">
          <span>Total de este pedido</span>
          <strong><Price value={productPriceUsd} country={orderCountry} clean /></strong>
        </div>
        <button className="button button--whatsapp" type="submit" disabled={isPreparingOrder}>
          {isPreparingOrder ? <LoaderCircle /> : <MessageCircle />} {isPreparingOrder ? "Preparando pedido…" : receiptFile ? "Enviar comprobante y pedido" : "Enviar pedido por WhatsApp"}
        </button>
        <small className="checkout-note">Después de transferir, adjunta el comprobante. Divedix verificará existencia, entrega y pago con este número de pedido.</small>
      </form>
      <CheckoutAnimation open={isPreparingOrder} itemCount={1} />
    </ModalShell>
  );
}

function ProductDetailModal({ product, country, countries, contact, onAddToCart, onBuyNow, onClose, now = Date.now() }) {
  const [quantity, setQuantity] = useState(1);
  const [addedQuantity, setAddedQuantity] = useState(0);
  const [shareStatus, setShareStatus] = useState("");

  useEffect(() => {
    setQuantity(1);
    setAddedQuantity(0);
    setShareStatus("");
  }, [product?.id]);

  if (!product) return null;
  const stock = Math.max(0, Number(product.stock || 0));
  const maxQuantity = Math.max(1, stock);
  const unitPriceUsd = effectiveValueInUsd(product, countries, now);
  const features = productFeatures(product);
  const category = productCategoryMeta[product.categoria]?.label || product.categoria || "Producto";
  const facts = [
    ["Marca", product.marca],
    ["Modelo", product.modelo],
    ["Referencia", product.sku],
    ["Categoría", category],
    ["Garantía", product.garantia],
    ["Entrega", product.entrega],
  ].filter(([, value]) => String(value || "").trim());

  const addSelectedQuantity = (event) => {
    if (stock <= 0) return;
    onAddToCart(product, event.currentTarget, quantity);
    setAddedQuantity(quantity);
  };

  const buySelectedQuantity = (event) => {
    if (stock <= 0) return;
    onBuyNow(product, event.currentTarget, quantity);
  };

  const shareProduct = async () => {
    const shareUrl = new URL("/", window.location.origin);
    shareUrl.searchParams.set("producto", product.id);
    shareUrl.hash = "store";
    const shareData = {
      title: `${product.nombre} | Divedix Store`,
      text: `Mira ${product.nombre} en Divedix Store.`,
      url: shareUrl.toString(),
    };
    setShareStatus("");
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        setShareStatus("Producto compartido.");
        trackMetric(`clicks/Compartir_Producto_${product.id}`);
        return;
      } catch (caught) {
        if (caught?.name === "AbortError") return;
      }
    }
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(shareUrl.toString());
      setShareStatus("Enlace del producto copiado.");
      trackMetric(`clicks/Compartir_Producto_${product.id}`);
    } catch {
      setShareStatus(`Comparte este enlace: ${shareUrl.toString()}`);
    }
  };

  const wholesaleGroup = {
    label: humanizeProductGroup(product.subcategoria || category),
    quantity,
    items: [{ name: product.nombre, quantity }],
  };

  return (
    <ModalShell open={Boolean(product)} onClose={onClose} label={`Detalles de ${product.nombre}`} className="modal-card--product-detail">
      <div className="product-detail__breadcrumb"><Store /> Divedix Store <ChevronRight /> {category}</div>
      <div className="product-detail">
        <div className="product-detail__media">
          <ProductVisual key={product.id} product={product} eager />
          <div className="product-detail__media-note"><ShieldCheck /><span><strong>Compra acompañada</strong><small>Confirmamos inventario y entrega contigo.</small></span></div>
        </div>

        <div className="product-detail__info">
          <span className="product-detail__category">{category}</span>
          <h2>{product.nombre}</h2>
          {(product.marca || product.modelo) && <p className="product-detail__identity">{[product.marca, product.modelo].filter(Boolean).join(" · ")}</p>}
          <OfferCountdown item={product} now={now} />
          <div className="product-detail__price"><small>Precio</small><strong><OfferPrice item={product} countries={countries} country={country} now={now} /></strong></div>
          <div className={stock > 3 ? "product-detail__stock is-available" : stock > 0 ? "product-detail__stock is-low" : "product-detail__stock is-out"}>
            <span />
            {stock > 0 ? `${stock} ${product.categoria === "digitales" ? "cupos" : "unidades"} disponibles` : "Temporalmente agotado"}
          </div>
          <p className="product-detail__description">{product.descripcion || "Consulta todos los detalles de este producto con nuestro equipo."}</p>

          {features.length > 0 && <section className="product-detail__features">
            <h3>Acerca de este artículo</h3>
            <ul>{features.map((feature, index) => <li key={`${feature}-${index}`}><Check /> <span>{feature}</span></li>)}</ul>
          </section>}

          {facts.length > 0 && <dl className="product-detail__facts">
            {facts.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
          </dl>}
        </div>

        <aside className="product-detail__buybox">
          <OfferCountdown item={product} now={now} compact />
          <div><small>Precio por unidad</small><strong><OfferPrice item={product} countries={countries} country={country} now={now} /></strong></div>
          <label>Cantidad</label>
          <div className="product-detail__quantity" aria-label="Cantidad del producto">
            <button type="button" onClick={() => setQuantity((current) => Math.max(1, current - 1))} disabled={quantity <= 1} aria-label="Restar una unidad"><Minus /></button>
            <strong>{quantity}</strong>
            <button type="button" onClick={() => setQuantity((current) => Math.min(maxQuantity, current + 1))} disabled={quantity >= maxQuantity || stock <= 0} aria-label="Agregar una unidad"><Plus /></button>
          </div>
          <div className="product-detail__total"><span>Total</span><strong><Price value={unitPriceUsd * quantity} country={country} clean /></strong></div>
          <div className="product-detail__actions">
            <button className="product-detail__add" type="button" onClick={addSelectedQuantity} disabled={stock <= 0}>
              <ShoppingCart /> {stock > 0 ? `Agregar ${quantity > 1 ? quantity : ""} al carrito` : "Producto agotado"}
            </button>
            <button className="product-detail__buy-now" type="button" onClick={buySelectedQuantity} disabled={stock <= 0}>
              <ShoppingBag /> Comprar ahora
            </button>
            <button className="product-detail__share" type="button" onClick={shareProduct}>
              <Share2 /> Compartir producto
            </button>
          </div>
          {addedQuantity > 0 && <p className="product-detail__added" role="status"><Check /> {addedQuantity} {addedQuantity === 1 ? "unidad agregada" : "unidades agregadas"}</p>}
          {shareStatus && <p className="product-detail__share-status" role="status">{shareStatus}</p>}
          {quantity >= 5 && stock >= 5 && (
            <section className="product-detail__wholesale" aria-label="Compra al por mayor">
              <span><Package /></span>
              <div>
                <small>PRECIO ESPECIAL DISPONIBLE</small>
                <strong>¿Quieres comprar por mayor?</strong>
                <p>Con {quantity} unidades puedes solicitar una cotización mayorista.</p>
              </div>
              <a
                href={wholesaleRequestLink(contact, wholesaleGroup)}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackMetric(`clicks/WhatsApp_Mayorista_${product.subcategoria || product.categoria || product.id}`)}
              >
                Quiero comprar por mayor <MessageCircle />
              </a>
            </section>
          )}
          <ul className="product-detail__assurances">
            <li><ShieldCheck /> Compra segura y confirmación directa</li>
            <li><Package /> Entrega coordinada según disponibilidad</li>
            <li><MessageCircle /> Soporte humano por WhatsApp</li>
          </ul>
        </aside>
      </div>
    </ModalShell>
  );
}

function CartModal({ open, items, country, countries, contact, paymentMethods, onClose, onQuantity, onRemove, onClear, onBrowseServices, now = Date.now() }) {
  const [name, setName] = useState("");
  const [hasReferral, setHasReferral] = useState("no");
  const [referralName, setReferralName] = useState("");
  const [orderCountryCode, setOrderCountryCode] = useState(country.code);
  const [paymentId, setPaymentId] = useState(() => paymentMethods?.find((method) => method.activo !== false)?.id || "");
  const [receiptFile, setReceiptFile] = useState(null);
  const [orderNumber, setOrderNumber] = useState(createOrderNumber);
  const [sendError, setSendError] = useState("");
  const [isPreparingOrder, setIsPreparingOrder] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOrderCountryCode(country.code);
    setPaymentId((current) => paymentMethods?.some((method) => method.id === current && method.activo !== false)
      ? current
      : paymentMethods?.find((method) => method.activo !== false)?.id || "");
    setReceiptFile(null);
    setOrderNumber(createOrderNumber());
    setSendError("");
    setHasReferral("no");
    setReferralName("");
  }, [open, country.code, paymentMethods]);

  useEffect(() => {
    if (!open) setIsPreparingOrder(false);
  }, [open]);

  const orderCountry = countries.find((item) => item.code === orderCountryCode) || country;
  const selectedPayment = paymentMethods?.find((method) => method.id === paymentId);
  const total = items.reduce((sum, item) => sum + Number(item.priceUsd || 0) * Number(item.quantity || 1), 0);
  const itemCount = items.reduce((sum, item) => sum + Number(item.quantity || 1), 0);
  const hasServices = items.some((item) => item.kind === "service");
  const wholesaleGroups = useMemo(() => {
    const groups = new Map();
    items.filter((item) => item.kind === "product").forEach((item) => {
      const id = item.subcategory || item.category || "productos";
      const current = groups.get(id) || {
        id,
        label: item.subcategoryLabel || item.categoryLabel || productCategoryMeta[item.category]?.label || humanizeProductGroup(id),
        quantity: 0,
        items: [],
      };
      current.quantity += Number(item.quantity || 1);
      current.items.push(item);
      groups.set(id, current);
    });
    return [...groups.values()].filter((group) => group.quantity >= 5);
  }, [items]);

  const submit = async (event) => {
    event.preventDefault();
    if (!items.length) return;
    const totalText = `${orderCountry.symbol}${new Intl.NumberFormat("es-419", { maximumFractionDigits: 2 }).format(total * Number(orderCountry.rate || 1))} ${orderCountry.currency}`;
    const lines = items.map((item) => {
      const quantity = Number(item.quantity || 1);
      const detail = item.kind === "service" ? ` · ${item.planName}` : quantity > 1 ? ` · Cantidad ${quantity}` : "";
      const lineTotal = Number(item.priceUsd || 0) * quantity * Number(orderCountry.rate || 1);
      return `• ${item.name}${detail} — ${orderCountry.symbol}${new Intl.NumberFormat("es-419", { maximumFractionDigits: 2 }).format(lineTotal)} ${orderCountry.currency}`;
    }).join("\n");
    const customer = name.trim() ? `\n▸ *Cliente:* ${cleanOrderText(name)}` : "";
    const referredBy = referralLine(hasReferral, referralName);
    const receiptLine = receiptFile ? "\n▸ *Comprobante:* Seleccionado para adjuntar" : "\n▸ *Comprobante:* Pendiente";
    const message = `*DIVEDIX STORE | CARRITO DE COMPRA*\n━━━━━━━━━━━━━━\n▸ *Pedido:* ${orderNumber}\n▸ *Artículos:* ${itemCount}\n▸ *Pago elegido:* ${selectedPayment?.nombre || "Por confirmar"}${receiptLine}${customer}${referredBy}\n\n*DETALLE DEL PEDIDO*\n${lines}\n\n▸ *TOTAL VIGENTE:* ${totalText}\n━━━━━━━━━━━━━━\n✓ Solicito confirmar inventario, ofertas vigentes, activaciones y entrega de este pedido.`;
    trackMetric("clicks/WhatsApp_Carrito");
    setSendError("");
    setIsPreparingOrder(true);
    await waitFor(PURCHASE_ANIMATION_DURATION);
    try {
      await sendOrder({ receiptFile, message, contact, orderNumber });
    } catch {
      setSendError("No pudimos abrir el envío. Inténtalo nuevamente.");
    } finally {
      setIsPreparingOrder(false);
    }
  };

  return (
    <ModalShell open={open} onClose={onClose} label="Carrito de compra" className="modal-card--cart">
      <div className="modal-kicker">TU SELECCIÓN</div>
      <div className="cart-modal__head">
        <div><h2>Carrito de compra</h2><p>Revisa todo y envía un solo pedido por WhatsApp.</p></div>
        {items.length > 0 && <button type="button" onClick={onClear}>Vaciar</button>}
      </div>
      {!items.length ? (
        <div className="cart-empty"><ShoppingCart /><strong>Tu carrito está vacío.</strong><button className="button button--primary" type="button" onClick={onClose}>Seguir comprando</button></div>
      ) : (
        <form className="order-form cart-form" onSubmit={submit}>
          <div className="cart-items">
            {items.map((item) => (
              <article className="cart-item" key={item.key}>
                <span className="cart-item__visual">
                  {item.image ? <DeferredImage src={item.image} eager alt="" /> : item.kind === "service" ? <Play /> : <ShoppingBag />}
                </span>
                <div className="cart-item__copy">
                  <small>{item.kind === "service" ? "STREAMING" : productCategoryMeta[item.category]?.label || "PRODUCTO"}</small>
                  <strong>{item.name}</strong>
                  {item.planName && <span>{item.planName}</span>}
                  {item.offerItem && <OfferCountdown item={item.offerItem} now={now} compact />}
                  <b><Price value={Number(item.priceUsd || 0) * Number(item.quantity || 1)} country={orderCountry} clean /></b>
                </div>
                {item.kind === "product" ? (
                  <div className="cart-quantity" aria-label={`Cantidad de ${item.name}`}>
                    <button type="button" onClick={() => onQuantity(item.key, -1)} aria-label="Restar uno"><Minus /></button>
                    <span>{item.quantity}</span>
                    <button type="button" onClick={() => onQuantity(item.key, 1)} disabled={Number(item.quantity) >= Number(item.maxStock || 99)} aria-label="Sumar uno"><Plus /></button>
                  </div>
                ) : <span className="cart-item__service-check"><Check /> Plan elegido</span>}
                <button className="cart-item__remove" type="button" onClick={() => onRemove(item.key)} aria-label={`Quitar ${item.name}`}><X /></button>
              </article>
            ))}
          </div>
          {wholesaleGroups.map((group) => (
            <section className="cart-wholesale" key={group.id} aria-label={`Compra al por mayor de ${group.label}`}>
              <div className="cart-wholesale__intro">
                <span><Package /></span>
                <div>
                  <small>COTIZACIÓN MAYORISTA DESBLOQUEADA</small>
                  <strong>¿Quieres comprar {group.label.toLowerCase()} por mayor?</strong>
                  <p>Ya seleccionaste {group.quantity} unidades de este tipo.</p>
                </div>
              </div>
              <ol className="cart-wholesale__steps" aria-label="Pasos de compra al por mayor">
                <li><b>1</b><span><strong>Confirmamos</strong><small>Modelos, cantidades e inventario.</small></span></li>
                <li><b>2</b><span><strong>Cotizamos</strong><small>Recibes el precio mayorista disponible.</small></span></li>
                <li><b>3</b><span><strong>Coordinamos</strong><small>Pago y entrega directamente contigo.</small></span></li>
              </ol>
              <a
                className="cart-wholesale__action"
                href={wholesaleRequestLink(contact, group)}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackMetric(`clicks/WhatsApp_Mayorista_${group.id}`)}
              >
                Quiero comprar por mayor <MessageCircle />
              </a>
            </section>
          ))}
          <aside className={hasServices ? "cart-streaming is-added" : "cart-streaming"}>
            <span><Play /></span>
            <div><strong>{hasServices ? "Streaming añadido" : "¿Quieres agregar streaming?"}</strong><small>{hasServices ? "Puedes sumar otros planes al mismo pedido." : "Elige Netflix, Prime Video, música, IPTV y más."}</small></div>
            <button type="button" onClick={onBrowseServices}>{hasServices ? "Ver más" : "Agregar"} <ArrowDown /></button>
          </aside>
          <div className="checkout-details">
            <label>País y moneda<select value={orderCountryCode} onChange={(event) => setOrderCountryCode(event.target.value)}>{countries.map((item) => <option key={item.code} value={item.code}>{item.flag} {item.name} · {item.currency}</option>)}</select></label>
            <label>Tu nombre <small>(opcional)</small><input value={name} onChange={(event) => setName(event.target.value)} placeholder="¿Cómo te llamas?" autoComplete="name" /></label>
          </div>
          <ReferralQuestion value={hasReferral} onChange={setHasReferral} name={referralName} onNameChange={setReferralName} />
          <PaymentSelector methods={paymentMethods} paymentId={paymentId} onSelect={setPaymentId} step={1} />
          <ReceiptUpload file={receiptFile} onChange={setReceiptFile} orderNumber={orderNumber} step={2} />
          {sendError && <p className="form-error">{sendError}</p>}
          <div className="modal-total checkout-total"><span>Total del carrito · {itemCount} {itemCount === 1 ? "artículo" : "artículos"}</span><strong><Price value={total} country={orderCountry} clean /></strong></div>
          <button className="button button--whatsapp" type="submit" disabled={isPreparingOrder}>{isPreparingOrder ? <LoaderCircle /> : <MessageCircle />} {isPreparingOrder ? "Preparando pedido…" : receiptFile ? "Enviar comprobante y carrito" : "Enviar carrito por WhatsApp"}</button>
          <small className="checkout-note">Divedix confirmará inventario, activaciones, entrega y pago usando el número de pedido.</small>
        </form>
      )}
      <CheckoutAnimation open={isPreparingOrder} itemCount={itemCount} />
    </ModalShell>
  );
}

function MovieModal({ movie, onClose }) {
  return (
    <ModalShell open={Boolean(movie)} onClose={onClose} label="Sinopsis de película">
      {movie && (
        <>
          <div className="modal-kicker">PELÍCULA DESTACADA</div>
          <div className="movie-modal-head">
            {movie.imagen ? <DeferredImage src={movie.imagen} eager alt="" /> : <span><Film /></span>}
            <div>
              <h2>{movie.titulo}</h2>
              <p>Disponible dentro de nuestras opciones de entretenimiento.</p>
            </div>
          </div>
          <div className="movie-synopsis">
            <strong>Sinopsis</strong>
            <p>{movie.sinopsis || "Consulta la disponibilidad y los detalles por WhatsApp."}</p>
          </div>
          <button className="button button--primary" type="button" onClick={onClose}>
            Ver plataformas <Clapperboard />
          </button>
        </>
      )}
    </ModalShell>
  );
}

const authErrorMessage = (caught) => {
  const messages = {
    "auth/email-already-in-use": "Este correo ya está registrado. Inicia sesión o recupera tu contraseña.",
    "auth/invalid-credential": "El correo o la contraseña no coinciden.",
    "auth/invalid-login-credentials": "El correo o la contraseña no coinciden.",
    "auth/invalid-email": "Escribe un correo electrónico válido.",
    "auth/missing-password": "Escribe tu contraseña.",
    "auth/network-request-failed": "No pudimos conectar. Revisa tu conexión e inténtalo otra vez.",
    "auth/operation-not-allowed": "Este método de acceso todavía no está habilitado.",
    "auth/configuration-not-found": "El acceso todavía no está configurado en Firebase. Inténtalo nuevamente en unos minutos.",
    "auth/popup-closed-by-user": "La ventana de Google se cerró antes de completar el acceso.",
    "auth/unauthorized-domain": "Este dominio todavía no está autorizado para iniciar sesión.",
    "auth/too-many-requests": "Se hicieron demasiados intentos. Espera unos minutos e inténtalo nuevamente.",
    "auth/user-not-found": "No encontramos una cuenta registrada con ese correo.",
    "auth/weak-password": "La contraseña debe tener al menos 6 caracteres.",
    "auth/wrong-password": "El correo o la contraseña no coinciden.",
  };
  return messages[caught?.code] || caught?.message?.replace("Firebase: ", "") || "No pudimos completar la solicitud.";
};

function CommentModal({ open, onClose }) {
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    return subscribeToUser(setUser);
  }, [open]);

  const changeAuthMode = (mode) => {
    setAuthMode(mode);
    setError("");
    setNotice("");
    setPassword("");
    setPasswordConfirmation("");
  };

  const submitAuth = async (event) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError("Escribe tu correo electrónico.");
      return;
    }
    if (authMode === "register" && password !== passwordConfirmation) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    if (authMode !== "reset" && password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (authMode === "reset") {
        await requestPasswordReset(normalizedEmail);
        setNotice("Te enviamos un enlace para crear una nueva contraseña. Revisa también tu carpeta de correo no deseado.");
        setAuthMode("login");
        setPassword("");
      } else {
        const credential = authMode === "register"
          ? await registerWithEmail(normalizedEmail, password)
          : await loginWithEmail(normalizedEmail, password);
        setUser(credential.user);
        setPassword("");
        setPasswordConfirmation("");
        setNotice(authMode === "register" ? "Tu cuenta está lista. Ya puedes publicar tu opinión." : "Sesión iniciada correctamente.");
      }
    } catch (caught) {
      setError(authErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const accessWithGoogle = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const credential = await loginWithGoogle();
      setUser(credential.user);
      setNotice("Accediste con Google. Ya puedes publicar tu opinión.");
    } catch (caught) {
      setError(authErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const closeSession = async () => {
    setBusy(true);
    setError("");
    try {
      await logout();
      setUser(null);
      setNotice("Sesión cerrada.");
    } catch (caught) {
      setError(authErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!user) {
      setError("Inicia sesión o crea una cuenta para publicar.");
      return;
    }
    if (name.trim().length < 2 || text.trim().length < 5) {
      setError("Escribe tu nombre y una opinión de al menos 5 caracteres.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await publishComment(user, text.trim(), name.trim());
      setName("");
      setText("");
      onClose();
    } catch (caught) {
      setError(authErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell open={open} onClose={onClose} label="Dejar una opinión">
      <div className="modal-kicker">OPINIONES VERIFICADAS</div>
      <h2>{user ? "Tu experiencia también cuenta." : "Entra para compartir tu experiencia."}</h2>
      {!user ? <>
        <p className="comment-intro">Para publicar una opinión necesitas una cuenta con correo y contraseña, o puedes continuar con Google.</p>
        {authMode !== "reset" && <div className="auth-switch" role="tablist" aria-label="Acceso a opiniones">
          <button type="button" role="tab" aria-selected={authMode === "login"} className={authMode === "login" ? "is-active" : ""} onClick={() => changeAuthMode("login")}>Iniciar sesión</button>
          <button type="button" role="tab" aria-selected={authMode === "register"} className={authMode === "register" ? "is-active" : ""} onClick={() => changeAuthMode("register")}>Crear cuenta</button>
        </div>}
        <form className="auth-panel" onSubmit={submitAuth}>
          {authMode === "reset" && <div className="auth-panel__heading"><strong>Recupera tu contraseña</strong><span>Recibirás un enlace seguro en tu correo.</span></div>}
          <label>
            Correo electrónico
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="tu@correo.com" autoComplete="email" required />
          </label>
          {authMode !== "reset" && <label>
            Contraseña
            <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Mínimo 6 caracteres" autoComplete={authMode === "register" ? "new-password" : "current-password"} minLength="6" required />
          </label>}
          {authMode === "register" && <label>
            Confirmar contraseña
            <input type="password" value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} placeholder="Repite tu contraseña" autoComplete="new-password" minLength="6" required />
          </label>}
          <button className="button button--primary" type="submit" disabled={busy}>
            {busy ? "Procesando…" : authMode === "register" ? "Crear mi cuenta" : authMode === "reset" ? "Enviar enlace de recuperación" : "Iniciar sesión"}
          </button>
        </form>
        {authMode === "login" && <button className="auth-text-button" type="button" onClick={() => changeAuthMode("reset")}>¿Olvidaste tu contraseña?</button>}
        {authMode === "reset" ? <button className="auth-text-button" type="button" onClick={() => changeAuthMode("login")}>Volver a iniciar sesión</button> : <>
          <div className="auth-divider"><span>o</span></div>
          <button className="auth-google" type="button" onClick={accessWithGoogle} disabled={busy}><span>G</span> Continuar con Google</button>
        </>}
      </> : <>
        <div className="comment-account">
          <ShieldCheck />
          <span><strong>Sesión activa</strong><small>{user.email || user.displayName || "Cuenta de Google"}</small></span>
          <button type="button" onClick={closeSession} disabled={busy}>Cerrar sesión</button>
        </div>
        <p className="comment-intro">Tu correo no se mostrará. Solo publicaremos el nombre y la opinión que escribas.</p>
        <form className="comment-form" onSubmit={submit}>
          <label>
            Nombre que aparecerá públicamente
            <input value={name} onChange={(event) => setName(event.target.value)} maxLength="50" placeholder="Ej. María" autoComplete="name" />
          </label>
          <label>
            Tu opinión
            <textarea value={text} onChange={(event) => setText(event.target.value)} maxLength="500" rows="5" placeholder="¿Qué te gustó? ¿Cómo fue tu experiencia?" />
          </label>
          <button className="button button--primary" type="submit" disabled={busy}>{busy ? "Publicando…" : "Publicar opinión"}</button>
        </form>
      </>}
      {notice && <p className="form-notice" role="status">{notice}</p>}
      {error && <p className="form-error">{error}</p>}
    </ModalShell>
  );
}

function StandaloneTopbar({ catalog, country, setCountry, theme, setTheme, backLabel = "Volver a la tienda" }) {
  return (
    <header className="download-topbar special-topbar">
      <Brand />
      <div className="special-topbar__actions">
        <label className="country-picker">
          <span className="sr-only">Ver precios para</span>
          <Globe2 className="country-picker__globe" aria-hidden="true" />
          <select value={country.code} onChange={(event) => setCountry(event.target.value)}>
            {catalog.paises.map((item) => <option key={item.code} value={item.code}>{item.flag} {item.name}</option>)}
          </select>
          <ChevronDown className="country-picker__chevron" aria-hidden="true" />
        </label>
        <ThemeToggle theme={theme} setTheme={setTheme} />
        <a href="/"><ArrowLeft /> {backLabel}</a>
      </div>
    </header>
  );
}

function PerfumeApp() {
  useUniqueVisit();
  const catalog = useCatalog();
  const offerNow = useOfferClock();
  const [theme, setTheme] = usePersistentTheme();
  const [countryCode, setCountryCode] = useState(() => localStorage.getItem(COUNTRY_KEY) || "DO");
  const [query, setQuery] = useState("");
  const [family, setFamily] = useState("todos");
  const [productOrder, setProductOrder] = useState(null);
  const country = catalog.paises.find((item) => item.code === countryCode) || catalog.paises[0];
  const products = (catalog.perfumeProducts || []).filter((product) => product.activo !== false);
  const families = [...new Set(["Hombre", "Mujer", "Unisex", ...products.map((product) => product.familia).filter(Boolean)])];
  const normalizedFamily = family.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const perfumeTone = normalizedFamily.includes("hombre") || normalizedFamily.includes("masculino")
    ? "men"
    : normalizedFamily.includes("mujer") || normalizedFamily.includes("femenino")
      ? "women"
      : normalizedFamily.includes("unisex")
        ? "unisex"
        : "all";
  const visibleProducts = products.filter((product) => {
    const haystack = `${product.nombre || ""} ${product.marca || ""} ${product.familia || ""}`.toLowerCase();
    return (family === "todos" || product.familia === family) && haystack.includes(query.trim().toLowerCase());
  });

  useEffect(() => {
    document.title = "Perfumes | Divedix Store";
    const canonical = document.querySelector('link[rel="canonical"]');
    const openGraphUrl = document.querySelector('meta[property="og:url"]');
    canonical?.setAttribute("href", `${SITE_ORIGIN}/perfumes`);
    openGraphUrl?.setAttribute("content", `${SITE_ORIGIN}/perfumes`);
    window.scrollTo(0, 0);
    return () => {
      document.title = "Divedix Store — Tecnología y entretenimiento";
      canonical?.setAttribute("href", `${SITE_ORIGIN}/`);
      openGraphUrl?.setAttribute("content", `${SITE_ORIGIN}/`);
    };
  }, []);

  const changeCountry = (code) => {
    setCountryCode(code);
    localStorage.setItem(COUNTRY_KEY, code);
  };

  return (
    <div className={`special-page perfume-page perfume-page--${perfumeTone}`}>
      <StandaloneTopbar catalog={catalog} country={country} setCountry={changeCountry} theme={theme} setTheme={setTheme} />
      <main>
        <section className="special-hero perfume-hero">
          <div>
            <span className="special-kicker"><Sparkles /> PERFUMERÍA DIVEDIX</span>
            <h1>{catalog.perfumeConfig?.titulo}</h1>
            <p>{catalog.perfumeConfig?.descripcion}</p>
            <a href="#catalogo-perfumes" className="button button--primary">Ver perfumes <ArrowDown /></a>
          </div>
          <div className="perfume-hero__visual">
            <span className="perfume-orbit perfume-orbit--one" aria-hidden="true" />
            <span className="perfume-orbit perfume-orbit--two" aria-hidden="true" />
            <img src="/img/perfume-hero-water-transparent.png" alt="Perfume azul rodeado por una composición de agua" decoding="async" fetchPriority="high" />
          </div>
        </section>
        <section className="special-catalog" id="catalogo-perfumes">
          <div className="special-catalog__head">
            <div><span>CATÁLOGO DE FRAGANCIAS</span><h2>Elige tu próximo perfume.</h2></div>
            <label className="catalog-search"><Search /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar perfume o marca" /></label>
          </div>
          {families.length > 0 && <div className="special-filter-row">
            <button className={family === "todos" ? "is-active" : ""} type="button" onClick={() => setFamily("todos")}>Todos</button>
            {families.map((item) => <button className={family === item ? "is-active" : ""} type="button" key={item} onClick={() => setFamily(item)}>{item}</button>)}
          </div>}
          {visibleProducts.length ? <div className="store-grid perfume-grid">{visibleProducts.map((product) => <article className="store-product perfume-card" key={product.id}>
            <ProductVisual product={{ ...product, categoria: "perfumes" }} />
            <OfferCountdown item={product} now={offerNow} compact />
            <div className="store-product__copy">
              <span>{[product.marca, product.familia].filter(Boolean).join(" · ") || "Perfumería"}</span>
              <h3>{product.nombre}</h3>
              <div className="perfume-attributes">{product.concentracion && <small>{product.concentracion}</small>}{product.tamano && <small>{product.tamano}</small>}</div>
              <ProductDescription>{product.descripcion}</ProductDescription>
            </div>
            <div className="store-product__footer"><div><small>Precio</small><strong><OfferPrice item={product} countries={catalog.paises} country={country} now={offerNow} /></strong></div><button type="button" disabled={Number(product.stock || 0) <= 0} onClick={() => { trackMetric(`clicks/Ver_Perfume_${product.id}`); trackUniqueProductView(product.id); setProductOrder({ ...product, categoria: "perfumes" }); }}>{Number(product.stock || 0) > 0 ? "Comprar" : "Agotado"} <ArrowUpRight /></button></div>
            <small className={Number(product.stock || 0) > 3 ? "store-stock" : "store-stock store-stock--low"}>{Number(product.stock || 0) > 0 ? `${product.stock} unidades disponibles` : "Temporalmente agotado"}</small>
          </article>)}</div> : <div className="store-empty perfume-empty"><Sparkles /><strong>{products.length ? "No encontramos perfumes con ese filtro." : "Muy pronto publicaremos los perfumes disponibles."}</strong><span>El catálogo se administra desde el panel Divedix.</span></div>}
        </section>
      </main>
      <Footer contact={catalog.contacto} socialLinks={catalog.socialLinks} />
      <ProductOrderModal product={productOrder} country={country} countries={catalog.paises} contact={catalog.contacto} paymentMethods={catalog.metodosPago} now={offerNow} onClose={() => setProductOrder(null)} />
    </div>
  );
}

function GameRechargeIcon({ game }) {
  const [imageFailed, setImageFailed] = useState(false);
  const Icon = GAME_ICON_COMPONENTS[game.icono] || Gamepad2;
  return game.imagen && !imageFailed
    ? <img src={game.imagen} alt="" onError={() => setImageFailed(true)} />
    : <Icon />;
}

function GameRechargeApp({ gameSlug = "" }) {
  useUniqueVisit();
  const catalog = useCatalog();
  const offerNow = useOfferClock();
  const config = catalog.gameRechargeConfig || {};
  const games = (config.juegos || []).filter((item) => item.activo !== false);
  const game = games.find((item) => item.slug === gameSlug || item.id === gameSlug);
  const [theme, setTheme] = usePersistentTheme();
  const [countryCode, setCountryCode] = useState(() => localStorage.getItem(COUNTRY_KEY) || "DO");
  const [playerId, setPlayerId] = useState("");
  const [playerName, setPlayerName] = useState("");
  const [playerPhone, setPlayerPhone] = useState("");
  const [playerRegion, setPlayerRegion] = useState("");
  const [playerConfirmed, setPlayerConfirmed] = useState(false);
  const [hasReferral, setHasReferral] = useState("no");
  const [referralName, setReferralName] = useState("");
  const [packageId, setPackageId] = useState("");
  const [paymentId, setPaymentId] = useState(() => catalog.metodosPago?.find((method) => method.activo !== false)?.id || "");
  const [receiptFile, setReceiptFile] = useState(null);
  const [orderNumber] = useState(createOrderNumber);
  const [error, setError] = useState("");
  const [isPreparingOrder, setIsPreparingOrder] = useState(false);
  const country = catalog.paises.find((item) => item.code === countryCode) || catalog.paises[0];
  const packages = (game?.paquetes || []).filter((item) => item.activo !== false);
  const selectedPackage = packages.find((item) => item.id === packageId);
  const selectedAmount = Number(selectedPackage?.cantidad ?? selectedPackage?.diamantes ?? 0);
  const selectedBonus = Number(selectedPackage?.bonus || 0);
  const selectedTotal = selectedAmount + selectedBonus;
  const selectedPayment = catalog.metodosPago?.find((method) => method.id === paymentId);
  const unit = game?.unidad || "créditos";

  useEffect(() => {
    const pagePath = game ? `/recargas-juegos/${game.slug}` : "/recargas-juegos";
    document.title = `${game ? `Recarga ${game.nombre}` : "Recargas de juegos"} | Divedix Store`;
    const canonical = document.querySelector('link[rel="canonical"]');
    const openGraphUrl = document.querySelector('meta[property="og:url"]');
    const robots = document.querySelector('meta[name="robots"]');
    canonical?.setAttribute("href", `${SITE_ORIGIN}${pagePath}`);
    openGraphUrl?.setAttribute("content", `${SITE_ORIGIN}${pagePath}`);
    if (!config.activo) robots?.setAttribute("content", "noindex,nofollow");
    window.scrollTo(0, 0);
    return () => {
      document.title = "Divedix Store — Tecnología y entretenimiento";
      canonical?.setAttribute("href", `${SITE_ORIGIN}/`);
      openGraphUrl?.setAttribute("content", `${SITE_ORIGIN}/`);
      robots?.setAttribute("content", "index,follow");
    };
  }, [config.activo, game]);

  const changeCountry = (code) => {
    setCountryCode(code);
    localStorage.setItem(COUNTRY_KEY, code);
  };
  const resetConfirmation = () => {
    setPlayerConfirmed(false);
    setError("");
  };
  const confirmPlayer = () => {
    if (playerId.trim().length < 3) return setError("Escribe el ID o UID de la cuenta.");
    if (playerName.trim().length < 2) return setError("Escribe manualmente el nombre exacto del jugador.");
    if (playerPhone.replace(/\D/g, "").length < 7) return setError("Escribe un número de teléfono válido.");
    setError("");
    setPlayerConfirmed(true);
    return undefined;
  };
  const submit = async (event) => {
    event.preventDefault();
    if (isPreparingOrder) return undefined;
    if (!selectedPackage) return setError("Selecciona primero un paquete de recarga.");
    if (!playerConfirmed) return setError("Confirma los datos del jugador antes de enviar el pedido.");
    const priceUsd = effectiveValueInUsd(selectedPackage, catalog.paises, Date.now());
    const price = Number(selectedPackage.precio || 0) > 0 ? `${country.symbol}${new Intl.NumberFormat("es-419", { maximumFractionDigits: 2 }).format(priceUsd * country.rate)} ${country.currency}` : "Por confirmar";
    const message = `*DIVEDIX STORE | RECARGA DE JUEGO*\n━━━━━━━━━━━━━━\n▸ *Pedido:* ${orderNumber}\n▸ *Juego:* ${game.nombre}\n▸ *Jugador:* ${cleanOrderText(playerName)}\n▸ *ID / UID:* ${cleanOrderText(playerId, 40)}\n▸ *Región o servidor:* ${cleanOrderText(playerRegion) || "No indicada"}\n▸ *Teléfono:* ${cleanOrderText(playerPhone, 30)}\n\n▸ *Paquete:* ${selectedPackage.nombre}\n▸ *Cantidad:* ${selectedAmount.toLocaleString("es-419")} ${unit}\n▸ *Bono:* ${selectedBonus.toLocaleString("es-419")} ${unit}\n▸ *Total:* ${selectedTotal.toLocaleString("es-419")} ${unit}\n▸ *Precio vigente:* ${price}\n▸ *Pago elegido:* ${selectedPayment?.nombre || "Por confirmar"}\n▸ *Comprobante:* ${receiptFile ? "Seleccionado para adjuntar" : "Pendiente"}${referralLine(hasReferral, referralName)}\n━━━━━━━━━━━━━━\n✓ Datos escritos y confirmados manualmente por el cliente.`;
    trackMetric(`clicks/WhatsApp_Recarga_${game.id}`);
    setError("");
    setIsPreparingOrder(true);
    await waitFor(PURCHASE_ANIMATION_DURATION);
    try { await sendOrder({ receiptFile, message, contact: catalog.contacto, orderNumber }); }
    catch { setError("No pudimos abrir el envío. Inténtalo nuevamente."); }
    finally { setIsPreparingOrder(false); }
    return undefined;
  };

  if (!config.activo) return <div className="special-page freefire-page"><StandaloneTopbar catalog={catalog} country={country} setCountry={changeCountry} theme={theme} setTheme={setTheme} /><main className="feature-locked"><div><span><LockKeyhole /></span><small>SECCIÓN EN DESARROLLO</small><h1>Las recargas de juegos llegan pronto.</h1><p>Estamos preparando los paquetes disponibles.</p><a className="button button--primary" href="/"><ArrowLeft /> Volver a Divedix Store</a></div></main><Footer contact={catalog.contacto} socialLinks={catalog.socialLinks} /></div>;

  if (!gameSlug) return <div className="special-page game-recharge-page">
    <StandaloneTopbar catalog={catalog} country={country} setCountry={changeCountry} theme={theme} setTheme={setTheme} />
    <main>
      <section className="game-recharge-hero"><div><span className="special-kicker"><Gamepad2 /> RECARGAS DIRECTAS</span><h1>{config.titulo || "Recargas de juegos"}</h1><p>{config.descripcion}</p><a className="button button--primary" href="#juegos">Elegir mi juego <ArrowDown /></a></div><div className="game-recharge-hero__icons" aria-hidden="true">{games.slice(0, 4).map((item) => <span key={item.id} style={{ "--game-color": item.color }}><GameRechargeIcon game={item} /></span>)}</div></section>
      <section className="game-recharge-catalog" id="juegos"><div className="special-catalog__head"><div><span>RECARGAS DISPONIBLES</span><h2>Selecciona tu juego favorito.</h2></div><p>{config.instrucciones}</p></div><div className="game-recharge-grid">{games.map((item) => <a href={`/recargas-juegos/${item.slug}`} className="game-recharge-card" key={item.id} style={{ "--game-color": item.color || "#287ce5" }}><span className="game-recharge-card__icon"><GameRechargeIcon game={item} /></span><small>RECARGA MANUAL</small><h3>{item.nombre}</h3><p>{item.descripcion}</p><strong>Ver paquetes <ArrowUpRight /></strong></a>)}</div></section>
    </main>
    <Footer contact={catalog.contacto} socialLinks={catalog.socialLinks} />
  </div>;

  if (!game) return <div className="special-page game-recharge-page"><StandaloneTopbar catalog={catalog} country={country} setCountry={changeCountry} theme={theme} setTheme={setTheme} /><main className="feature-locked"><div><span><Gamepad2 /></span><small>JUEGO NO DISPONIBLE</small><h1>Esta recarga todavía no está publicada.</h1><a className="button button--primary" href="/recargas-juegos"><ArrowLeft /> Ver otros juegos</a></div></main><Footer contact={catalog.contacto} socialLinks={catalog.socialLinks} /></div>;

  return <div className="special-page freefire-page game-checkout-page" style={{ "--game-color": game.color || "#287ce5" }}>
    <StandaloneTopbar catalog={catalog} country={country} setCountry={changeCountry} theme={theme} setTheme={setTheme} />
    <main>
      <section className="special-hero game-detail-hero"><div><a className="game-detail-back" href="/recargas-juegos"><ArrowLeft /> Todos los juegos</a><span className="special-kicker"><Gamepad2 /> RECARGA MANUAL</span><h1>{game.nombre}</h1><p>{game.descripcion}</p></div><div className="game-detail-visual" aria-hidden="true"><GameRechargeIcon game={game} /></div></section>
      <section className="freefire-checkout">
        <div className="freefire-checkout__intro"><span>DATOS DE LA RECARGA</span><h2>Completa la cuenta manualmente.</h2><p>Escribe el ID, el nombre y la región exactamente como aparecen en {game.nombre}. Revisaremos los datos antes de procesar el pedido.</p></div>
        <form className="freefire-form" onSubmit={submit}>
          <fieldset><legend><span>1</span> Elige la recarga</legend>{packages.length ? <div className="diamond-packages">{packages.map((item) => { const amount = Number(item.cantidad ?? item.diamantes ?? 0); const total = amount + Number(item.bonus || 0); return <button type="button" className={packageId === item.id ? "is-selected" : ""} key={item.id} onClick={() => { setPackageId(item.id); resetConfirmation(); }}><span>◆ {item.nombre || `${amount} ${unit}`}</span><small>{item.bonus ? `+${Number(item.bonus).toLocaleString("es-419")} de bono` : "Recarga directa"}</small><em>{total > 0 ? `Total ${total.toLocaleString("es-419")} ${unit}` : "Cantidad por confirmar"}</em><OfferCountdown item={item} now={offerNow} compact /><strong>{Number(item.precio || 0) > 0 ? <OfferPrice item={item} countries={catalog.paises} country={country} now={offerNow} /> : "Precio por confirmar"}</strong></button>; })}</div> : <div className="freefire-no-packages">Los paquetes de este juego se publicarán desde el panel de administración.</div>}{selectedPackage && <div className="freefire-package-confirm"><span>PAQUETE SELECCIONADO</span><strong>{selectedPackage.nombre}</strong><small>{selectedTotal > 0 ? `${selectedTotal.toLocaleString("es-419")} ${unit} en total` : "Cantidad por confirmar"}</small><OfferCountdown item={selectedPackage} now={offerNow} compact /></div>}</fieldset>
          <fieldset className={!selectedPackage ? "freefire-player-step is-locked" : "freefire-player-step"}><legend><span>2</span> Datos de la cuenta</legend>{!selectedPackage && <p className="freefire-step-note">Selecciona un paquete para continuar.</p>}<div className="freefire-player-fields"><label>ID o UID del jugador<input disabled={!selectedPackage} value={playerId} onChange={(event) => { setPlayerId(event.target.value.slice(0, 40)); resetConfirmation(); }} placeholder="Escribe el ID exacto" required /></label><label>Nombre del jugador<input disabled={!selectedPackage} value={playerName} onChange={(event) => { setPlayerName(event.target.value); resetConfirmation(); }} placeholder="Escríbelo manualmente" required /></label><label>Número de teléfono<input disabled={!selectedPackage} inputMode="tel" value={playerPhone} onChange={(event) => { setPlayerPhone(event.target.value); resetConfirmation(); }} placeholder="Ej. +1 809 000 0000" required /></label><label>Región o servidor<input disabled={!selectedPackage} value={playerRegion} onChange={(event) => { setPlayerRegion(event.target.value); resetConfirmation(); }} placeholder="Opcional, si aplica" /></label></div><p className="freefire-manual-note">No hacemos búsquedas automáticas. Revisa cuidadosamente el nombre y el ID antes de confirmar.</p><button className="freefire-confirm-player" type="button" disabled={!selectedPackage} onClick={confirmPlayer}><Check /> Confirmar datos del jugador</button>{playerConfirmed && <div className="player-confirm"><Check /><span><small>CUENTA A RECARGAR</small><strong>{playerName}</strong><em>ID {playerId}{playerRegion ? ` · ${playerRegion}` : ""}</em><em>{playerPhone}</em></span></div>}</fieldset>
          <ReferralQuestion value={hasReferral} onChange={setHasReferral} name={referralName} onNameChange={setReferralName} /><PaymentSelector methods={catalog.metodosPago} paymentId={paymentId} onSelect={setPaymentId} step={3} /><ReceiptUpload file={receiptFile} onChange={setReceiptFile} orderNumber={orderNumber} step={4} />{error && <p className="form-error">{error}</p>}{selectedPackage && <div className="freefire-order-summary"><div><span>RESUMEN DEL PEDIDO</span><strong>{selectedPackage.nombre}</strong></div><dl><div><dt>Juego</dt><dd>{game.nombre}</dd></div><div><dt>Jugador</dt><dd>{playerConfirmed ? playerName : "Pendiente"}</dd></div><div><dt>ID / UID</dt><dd>{playerConfirmed ? playerId : "—"}</dd></div><div><dt>Región</dt><dd>{playerConfirmed ? playerRegion || "No indicada" : "—"}</dd></div><div><dt>Teléfono</dt><dd>{playerConfirmed ? playerPhone : "—"}</dd></div><div><dt>Referido</dt><dd>{hasReferral === "yes" ? referralName || "Pendiente" : "No"}</dd></div></dl><div className="modal-total checkout-total"><span>Total a pagar</span><strong>{Number(selectedPackage.precio || 0) > 0 ? <OfferPrice item={selectedPackage} countries={catalog.paises} country={country} now={offerNow} /> : "Por confirmar"}</strong></div></div>}<button className="button button--whatsapp" type="submit" disabled={!packages.length || !selectedPackage || !playerConfirmed || isPreparingOrder}>{isPreparingOrder ? <LoaderCircle /> : <MessageCircle />} {isPreparingOrder ? "Preparando pedido…" : "Enviar pedido por WhatsApp"}</button>
        </form>
        <CheckoutAnimation open={isPreparingOrder} itemCount={1} />
      </section>
    </main>
    <Footer contact={catalog.contacto} socialLinks={catalog.socialLinks} />
  </div>;
}

const OptimizedStoreSection = memo(StoreSection);
const OptimizedCatalog = memo(Catalog);
const OptimizedComboBuilder = memo(ComboBuilder);
const OptimizedFeaturedMovies = memo(FeaturedMovies);
const OptimizedFlowPlans = memo(FlowPlans);
const OptimizedPlayerSetup = memo(PlayerSetup);
const OptimizedTestimonials = memo(Testimonials);
const OptimizedFaq = memo(Faq);

function Storefront() {
  useUniqueVisit();
  const catalog = useCatalog();
  const offerNow = useOfferClock();
  const [countryCode, setCountryCode] = useState(() => localStorage.getItem(COUNTRY_KEY) || "DO");
  const [theme, setTheme] = usePersistentTheme();
  const [order, setOrder] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cart, setCart] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(CART_KEY) || "[]");
      return Array.isArray(saved) ? saved : [];
    } catch {
      return [];
    }
  });
  const [commentOpen, setCommentOpen] = useState(false);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const cartTargetRef = useRef(null);
  const sharedProductOpenedRef = useRef(false);

  const country = catalog.paises.find((item) => item.code === countryCode) || catalog.paises[0];
  const storeProducts = useMemo(() => [
    ...(catalog.storeProducts || []),
    ...(catalog.perfumeProducts || []).map((product) => ({ ...product, categoria: "perfumes" })),
  ], [catalog.storeProducts, catalog.perfumeProducts]);
  const liveCart = useMemo(() => cart.map((item) => {
    if (item.kind === "product") {
      const product = storeProducts.find((candidate) => candidate.id === item.productId);
      if (!product) return item;
      return {
        ...item,
        name: product.nombre,
        image: product.imagen || "",
        priceUsd: effectiveValueInUsd(product, catalog.paises, offerNow),
        maxStock: Math.max(1, Number(product.stock || 1)),
        offerItem: product,
      };
    }
    if (item.kind === "service") {
      const service = catalog.services.find((candidate) => candidate.id === item.serviceId);
      const parsedIndex = Number(item.planIndex ?? String(item.key || "").split(":").at(-1));
      const plan = service?.planes?.[Number.isFinite(parsedIndex) ? parsedIndex : 0];
      if (!service || !plan) return item;
      return {
        ...item,
        name: service.nombre,
        image: service.imagen || "",
        planName: plan.nombre,
        priceUsd: effectiveValueInUsd(plan, catalog.paises, offerNow),
        offerItem: plan,
      };
    }
    return item;
  }), [cart, catalog.paises, catalog.services, offerNow, storeProducts]);
  const cartCount = liveCart.reduce((sum, item) => sum + Number(item.quantity || 1), 0);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    const sharedProductId = new URLSearchParams(window.location.search).get("producto");
    if (!sharedProductId || sharedProductOpenedRef.current || !storeProducts.length) return;
    const sharedProduct = storeProducts.find((product) => String(product.id) === sharedProductId && product.activo !== false);
    if (!sharedProduct) return;
    sharedProductOpenedRef.current = true;
    setSelectedProduct(sharedProduct);
    trackMetric(`clicks/Abrir_Producto_Compartido_${sharedProduct.id}`);
    trackUniqueProductView(sharedProduct.id);
  }, [storeProducts]);

  useEffect(() => {
    if (["/descargar-iptv.html", "/descargar-flujo-tv.html"].includes(window.location.pathname)) {
      window.setTimeout(() => document.getElementById("instalacion")?.scrollIntoView(), 300);
    }
  }, []);

  const changeCountry = (code) => {
    setCountryCode(code);
    localStorage.setItem(COUNTRY_KEY, code);
  };

  const chooseService = useCallback((service, planIndex = 0) => {
    trackMetric(`clicks/Ver_Plataforma_${service.id}`);
    setOrder({ type: "service", service, planIndex });
  }, []);

  const addProductToCart = useCallback((product, source, requestedQuantity = 1) => {
    const key = `product:${product.id}`;
    const maxStock = Math.max(1, Number(product.stock || 1));
    const quantityToAdd = Math.max(1, Math.min(maxStock, Number(requestedQuantity || 1)));
    setCart((current) => {
      const existing = current.find((item) => item.key === key);
      if (existing) return current.map((item) => item.key === key ? {
        ...item,
        quantity: Math.min(Number(item.quantity || 1) + quantityToAdd, maxStock),
        maxStock,
        categoryLabel: productCategoryMeta[product.categoria]?.label || product.categoria || "Producto",
        subcategory: product.subcategoria || "",
        subcategoryLabel: catalog.storeFolders?.[product.categoria]?.find((folder) => folder.id === product.subcategoria)?.nombre
          || (product.subcategoria ? humanizeProductGroup(product.subcategoria) : ""),
      } : item);
      return [...current, {
        key,
        kind: "product",
        productId: product.id,
        name: product.nombre,
        image: product.imagen || "",
        category: product.categoria,
        categoryLabel: productCategoryMeta[product.categoria]?.label || product.categoria || "Producto",
        subcategory: product.subcategoria || "",
        subcategoryLabel: catalog.storeFolders?.[product.categoria]?.find((folder) => folder.id === product.subcategoria)?.nombre
          || (product.subcategoria ? humanizeProductGroup(product.subcategoria) : ""),
        priceUsd: effectiveValueInUsd(product, catalog.paises, Date.now()),
        quantity: quantityToAdd,
        maxStock,
      }];
    });
    trackMetric(`clicks/Carrito_Store_${product.id}`);
    trackUniqueProductView(product.id);
    animateProductToCart(product, source, cartTargetRef.current);
  }, [catalog.paises, catalog.storeFolders]);

  const buyProductNow = useCallback((product, source, requestedQuantity) => {
    addProductToCart(product, source, requestedQuantity);
    trackMetric(`clicks/Comprar_Ahora_${product.id}`);
    setSelectedProduct(null);
    setCartOpen(true);
  }, [addProductToCart]);

  const addServiceToCart = useCallback((service, planIndex, source) => {
    const plan = service.planes[planIndex];
    const key = `service:${service.id}:${planIndex}`;
    setCart((current) => current.some((item) => item.key === key) ? current : [...current, {
      key,
      kind: "service",
      serviceId: service.id,
      name: service.nombre,
      image: service.imagen || "",
      planName: plan.nombre,
      planIndex,
      priceUsd: effectiveValueInUsd(plan, catalog.paises, Date.now()),
      quantity: 1,
    }]);
    trackMetric(`clicks/Carrito_Plataforma_${service.id}`);
    animateProductToCart({ nombre: service.nombre }, source, cartTargetRef.current);
  }, [catalog.paises]);

  const updateCartQuantity = useCallback((key, change) => setCart((current) => current.flatMap((item) => {
    if (item.key !== key) return [item];
    const quantity = Math.min(Number(item.maxStock || 99), Number(item.quantity || 1) + change);
    return quantity > 0 ? [{ ...item, quantity }] : [];
  })), []);

  const browseStreaming = useCallback(() => {
    setCartOpen(false);
    window.setTimeout(() => document.getElementById("catalogo")?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  }, []);

  const openProduct = useCallback((product) => {
    trackMetric(`clicks/Ver_Producto_${product.id}`);
    trackUniqueProductView(product.id);
    setSelectedProduct(product);
  }, []);

  const selectMovie = useCallback((movie) => {
    trackMetric(`clicks/Ver_Pelicula_${movie.id || "destacada"}`);
    setSelectedMovie(movie);
  }, []);

  const openComment = useCallback(() => setCommentOpen(true), []);

  return (
    <>
      <TopBar
        catalog={catalog}
        country={country}
        setCountry={changeCountry}
        theme={theme}
        setTheme={setTheme}
      />
      <main>
        <Hero
          onExplore={() => document.getElementById("store")?.scrollIntoView({ behavior: "smooth" })}
          onComment={openComment}
        />
        <OptimizedStoreSection
          products={storeProducts}
          folders={catalog.storeFolders}
          countries={catalog.paises}
          country={country}
          now={offerNow}
        freeFireActive={catalog.gameRechargeConfig?.activo === true}
        onChoose={addProductToCart}
          onOpen={openProduct}
        />
        <OptimizedCatalog catalog={catalog} country={country} onChoose={chooseService} now={offerNow} />
        <OptimizedComboBuilder catalog={catalog} country={country} onOrder={setOrder} now={offerNow} />
        <OptimizedFeaturedMovies movies={catalog.peliculasDestacadas} onSelect={selectMovie} />
        <OptimizedFlowPlans catalog={catalog} country={country} onChoose={chooseService} now={offerNow} />
        <OptimizedPlayerSetup catalog={catalog} />
        <OptimizedTestimonials onOpenComment={openComment} />
        <OptimizedFaq items={catalog.faqs} />
      </main>
      <Footer contact={catalog.contacto} socialLinks={catalog.socialLinks} />
      <button
        ref={cartTargetRef}
        className={cartCount > 0 ? "floating-cart" : "floating-cart is-empty"}
        type="button"
        onClick={() => setCartOpen(true)}
        aria-label={cartCount > 0 ? `Abrir carrito con ${cartCount} artículos` : "Abrir carrito vacío"}
      >
        <ShoppingCart /><span>{cartCount}</span><strong>{cartCount > 0 ? "Ver carrito" : "Carrito"}</strong>
      </button>
      <OrderModal
        order={order}
        country={country}
        countries={catalog.paises}
        contact={catalog.contacto}
        paymentMethods={catalog.metodosPago}
        now={offerNow}
        onAddToCart={addServiceToCart}
        onClose={() => setOrder(null)}
      />
      <CartModal
        open={cartOpen}
        items={liveCart}
        country={country}
        countries={catalog.paises}
        contact={catalog.contacto}
        paymentMethods={catalog.metodosPago}
        now={offerNow}
        onClose={() => setCartOpen(false)}
        onQuantity={updateCartQuantity}
        onRemove={(key) => setCart((current) => current.filter((item) => item.key !== key))}
        onClear={() => setCart([])}
        onBrowseServices={browseStreaming}
      />
      <ProductDetailModal
        product={selectedProduct}
        country={country}
        countries={catalog.paises}
        contact={catalog.contacto}
        now={offerNow}
        onAddToCart={addProductToCart}
        onBuyNow={buyProductNow}
        onClose={() => setSelectedProduct(null)}
      />
      <MovieModal movie={selectedMovie} onClose={() => setSelectedMovie(null)} />
      <CommentModal open={commentOpen} onClose={() => setCommentOpen(false)} />
    </>
  );
}

export default function App() {
  const path = window.location.pathname.replace(/\/$/, "");
  const adminQuery = new URLSearchParams(window.location.search).get("admin") === "1";
  const downloadRoute = {
    "/instalar/iptv-smarters-pro": "iptv",
    "/descargar-iptv.html": "iptv",
    "/instalar/evo-pro-player": "evo",
    "/instalar/flujo-tv": "flujo",
    "/descargar-flujo-tv.html": "flujo",
  }[path];
  const dynamicDownloadPath = path.startsWith("/instalar/") ? path : "";
  const serviceSlug = path.startsWith("/plataformas/") ? path.split("/").filter(Boolean).at(-1) : "";
  const rechargeGameSlug = path.startsWith("/recargas-juegos/") ? path.split("/").filter(Boolean).at(-1) : "";
  if (adminQuery || path.startsWith("/admin")) {
    return <Suspense fallback={<div className="admin-loading">Cargando panel…</div>}><AdminApp /></Suspense>;
  }
  let publicPage;
  if (downloadRoute || dynamicDownloadPath) publicPage = <DownloadApp pageKey={downloadRoute} pagePath={dynamicDownloadPath} />;
  else if (serviceSlug) publicPage = <ServiceDetailApp serviceId={SLUG_SERVICES[serviceSlug] || serviceSlug} />;
  else if (path === "/perfumes") publicPage = <PerfumeApp />;
  else if (path === "/recargas-juegos") publicPage = <GameRechargeApp />;
  else if (rechargeGameSlug) publicPage = <GameRechargeApp gameSlug={rechargeGameSlug} />;
  else if (path === "/recargas-free-fire") publicPage = <GameRechargeApp gameSlug="free-fire" />;
  else if (path === "/sugerencias") publicPage = <SuggestionApp />;
  else publicPage = <Storefront />;
  return <PublicExtras>{publicPage}</PublicExtras>;
}
